/**
 * jv-runsheet-view.js — Interactive Runsheet Viewer (Prompt 238)
 * Fetches published runsheet via GAS bridge, renders timeline,
 * polls for updates, shows diff toasts.
 */
(function() {
  'use strict';

  /* ═══════════════════════════════════════════════════════════
     CONFIG
     ═══════════════════════════════════════════════════════════ */
  var root = document.getElementById('jvRunsheetView');
  if (!root) return;

  var audience = root.dataset.audience || 'bride';
  // A ?token= (or ?key=) in the URL is an explicit request to view THAT bride's
  // runsheet by share-token, regardless of who is logged in. Force token mode so
  // the logged-in customer's own embedded runsheet blob is never shown instead.
  // Without this, the admin "Copy bride link" and shared bridal-party links on
  // /pages/runsheet-view (audience="bride") resolved to the viewer's own runsheet.
  // 2026-06-02.
  try {
    var _rvqp = new URLSearchParams(window.location.search);
    if ((_rvqp.get('token') || _rvqp.get('key') || '').trim()) audience = 'token';
  } catch (_) {}
  /**
   * Build the sessionStorage key for the cached runsheet blob.
   *
   * P0 FIX 2026-08-06, cross-customer data exposure.
   *
   * This previously read:
   *     'jv_rs_blob_' + (tp.get('bride') || tp.get('brideName') || '')
   * at both the read and the write site. A bride's runsheet link is
   * /pages/runsheet-view?token=<UUID>, which carries NEITHER of those params,
   * so both lookups returned null, the empty-string fallback fired, and the key
   * collapsed to the bare literal 'jv_rs_blob_' shared by every bride in that
   * browser session. The token, the only thing identifying the bride, was never
   * part of the key.
   *
   * That is exposure rather than staleness because the cache-hit branch returns
   * BEFORE waitForBridge(fetchFull()), so GAS is never called: no token lookup,
   * no ownership check, nothing can intervene. Bride B opening her link in a
   * session where bride A had opened hers rendered A's runsheet entirely
   * client-side: party names, service times, artists, venue, wedding date.
   * Token links are deliberately shareable with bridesmaids, mothers and
   * vendors, so the two viewers are often unrelated people. This very likely
   * explains Bianca Dibou's report of seeing another bride's party.
   *
   * It went unnoticed because the ADMIN PREVIEW route passes ?bride=, which
   * keys correctly, so any check done from the admin side passed.
   *
   * FAIL CLOSED. The root cause was not the missing token param, it was the
   * empty-string fallback turning "I cannot identify this viewer" into "put
   * everyone in one bucket". An empty discriminator now means DO NOT CACHE,
   * never a shared bucket, so a future caller arriving without these params
   * loses the cache rather than silently re-collapsing into a leak.
   *
   * 'key' is included because audience is already derived from token-or-key
   * (see above), so both must be cacheable discriminators.
   *
   * @param {URLSearchParams} params
   * @return {string} the key, or '' meaning do not touch the cache at all.
   */
  function jvRvBlobCacheKey(params) {
    var disc = '';
    try {
      disc = String(
        params.get('token') || params.get('key') ||
        params.get('bride') || params.get('brideName') || ''
      ).trim();
    } catch (_) { return ''; }
    return disc ? ('jv_rs_blob_' + disc) : '';
  }

  /* ═══════════════════════════════════════════════════════════
     TOKEN-AUDIENCE CDN FAST PATH (2026-09-04)

     Fixes a real bride-facing minutes-long load on a fresh token link
     (SMS/email share, no Shopify login). Root cause traced live: a token
     visit had NO fast path at all, only sessionStorage helps a REPEAT
     visit in the same tab, so every FIRST visit blocked first paint on a
     slow GAS call, then a second slow GAS call (checkSignoffStatus), then
     a third (fetchInspirationPhotos), all firing sequentially. Same
     architecture already proven for the invoice page and the admin bride
     profile page: read a pre-computed blob from a Shopify metaobject via
     the Storefront API (no customer login needed, no GAS call at all),
     paint instantly, then still verify live in the background so any
     drift self-corrects within the same session (same "FAILED is not
     EMPTY" principle documented on the invoice/partner fast paths, a
     miss here is not a load failure, it just means no fast path is
     available yet for this bride, and the existing slow path runs
     exactly as it always did).

     The metaobject handle is DETERMINISTICALLY built from the exact URL
     token, so there is no possibility of the Bianca Dibou-class
     cross-bride cache collision documented above on jvRvBlobCacheKey.
     That bug was a SHARED bucket when the discriminator was empty; this
     handle is never used at all unless a real, specific token is present.
     ═══════════════════════════════════════════════════════════ */
  var JV_RS_SF_SHOP = 'jessicavegas.com';
  var JV_RS_SF_VER  = '2026-01';

  // MUST match GAS runsheetTokenViewCache_handle_ byte-for-byte.
  function jvRsTokenCacheHandle_(tok) {
    var slug = String(tok || '').trim().toLowerCase()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return 'runsheet-token-' + slug;
  }

  // Parse a metaobject field value that is EITHER plain JSON OR a JSON
  // string literal "JVGZ_<base64gzip>". Returns a Promise resolving to the
  // parsed object (or null). Same JVGZ_ pattern as every other cache in
  // this family (invoice view, admin bride profile, partner dashboard).
  function jvRsInflateIslandValue_(raw) {
    return new Promise(function (resolve) {
      try {
        if (!raw) { resolve(null); return; }
        var parsed = JSON.parse(raw);
        if (typeof parsed === 'string' && parsed.indexOf('JVGZ_') === 0) {
          if (typeof DecompressionStream === 'undefined') { resolve(null); return; }
          var b64 = parsed.slice(5);
          var bin = atob(b64);
          var bytes = new Uint8Array(bin.length);
          for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          var ds = new DecompressionStream('gzip');
          new Response(new Blob([bytes]).stream().pipeThrough(ds)).text()
            .then(function (txt) { try { resolve(JSON.parse(txt)); } catch (e) { resolve(null); } })
            .catch(function () { resolve(null); });
          return;
        }
        resolve(parsed); // plain object
      } catch (e) { resolve(null); }
    });
  }

  // Returns a Promise resolving to the runsheet blob object (same shape
  // render() already consumes), or null on any miss, no token, or error.
  // A null return is the caller's signal to fall through to the existing
  // live GAS path unchanged, never an error state of its own.
  function jvRsTryCdnFastPath_(tok) {
    var sfTok = (typeof window !== 'undefined' && window._jvRsSfToken) ? String(window._jvRsSfToken) : '';
    if (!sfTok || !tok) return Promise.resolve(null);
    var nf = (window._jvNativeFetch || window.fetch).bind(window);
    var handle = jvRsTokenCacheHandle_(tok);
    var q = 'query($h:MetaobjectHandleInput!){ metaobject(handle:$h){ ' +
            'b: field(key:"blob"){ value } ba: field(key:"built_at"){ value } } }';
    var body = { query: q, variables: { h: { type: 'runsheet_token_view_cache', handle: handle } } };
    return nf('https://' + JV_RS_SF_SHOP + '/api/' + JV_RS_SF_VER + '/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': sfTok },
      body: JSON.stringify(body)
    })
    .then(function (r) { return r.json(); })
    .then(function (d) {
      var mo = d && d.data && d.data.metaobject;
      if (!mo || !mo.b || !mo.b.value) return null;
      return jvRsInflateIslandValue_(mo.b.value).then(function (obj) {
        if (obj && obj.ok && obj.version && obj.timeline) return obj;
        return null;
      });
    })
    .catch(function () { return null; });
  }

  var pollInterval = parseInt(root.dataset.pollInterval, 10) || 90;
  var showPdf = root.dataset.showPdf !== 'false';
  var showBuilder = root.dataset.showBuilder === 'true';

  // EBM brides (customer tag "ebm-bride") get a read-only runsheet with NO
  // sign-off and NO change-request. That is the EBM partner's job, not the
  // bride's (Jess, 2026-07-28). Detected from the tags emitted by the section
  // into window.jvAuth.tags, so zero extra GAS call. Logged-in bride audience
  // only; token/team share links are unaffected.
  function jvRvIsEbmBride_() {
    if (audience !== 'bride') return false;
    var tags = (window.jvAuth && window.jvAuth.tags) || '';
    return (',' + String(tags).toLowerCase().replace(/\s+/g, '') + ',').indexOf(',ebm-bride,') !== -1;
  }

  /* ─── DOM refs ─────────────────────────────────────────── */
  var $skeleton = document.getElementById('jvRvSkeleton');
  var $empty = document.getElementById('jvRvEmpty');
  var $error = document.getElementById('jvRvError');
  var $errorDetail = document.getElementById('jvRvErrorDetail');
  var $content = document.getElementById('jvRvContent');
  var $retryBtn = document.getElementById('jvRvRetryBtn');

  var $brideName = document.getElementById('jvRvBrideName');
  var $weddingDate = document.getElementById('jvRvWeddingDate');
  var $readyBy = document.getElementById('jvRvReadyBy');
  var $versionChip = document.getElementById('jvRvVersionChip');
  var $versionDot = document.getElementById('jvRvVersionDot');
  var $versionText = document.getElementById('jvRvVersionText');

  var $artistRail = document.getElementById('jvRvArtistRail');
  var $timelineScroll = document.getElementById('jvRvTimelineScroll');
  var $colHeaders = document.getElementById('jvRvColHeaders');
  var $timeline = document.getElementById('jvRvTimeline');
  var $unscheduled = document.getElementById('jvRvUnscheduled');
  var $unscheduledList = document.getElementById('jvRvUnscheduledList');

  var $party = document.getElementById('jvRvParty');
  var $partyToggle = document.getElementById('jvRvPartyToggle');
  var $partyList = document.getElementById('jvRvPartyList');

  var $publishedAt = document.getElementById('jvRvPublishedAt');
  var $pdfBtn = document.getElementById('jvRvPdfBtn');
  var $builderBtn = document.getElementById('jvRvBuilderBtn');
  var $footer = document.getElementById('jvRvFooter');

  var $toast = document.getElementById('jvRvToast');
  var $toastText = document.getElementById('jvRvToastText');
  var $toastAction = document.getElementById('jvRvToastAction');
  var $toastClose = document.getElementById('jvRvToastClose');

  var $modalBackdrop = document.getElementById('jvRvModalBackdrop');
  var $modal = document.getElementById('jvRvModal');
  var $modalClose = document.getElementById('jvRvModalClose');
  var $modalPerson = document.getElementById('jvRvModalPerson');
  var $modalService = document.getElementById('jvRvModalService');
  var $modalTime = document.getElementById('jvRvModalTime');
  var $cardTrack = document.getElementById('jvRvCardTrack');
  var $cardDots = document.getElementById('jvRvCardDots');
  var $modalPdfBtn = document.getElementById('jvRvModalPdfBtn');
  var cardIndex = 0;
  var cardCount = 0;

  var $lightbox = document.getElementById('jvRvLightbox');
  var $lightboxImg = document.getElementById('jvRvLightboxImg');
  var $lightboxClose = document.getElementById('jvRvLightboxClose');
  var $lightboxPrev = document.getElementById('jvRvLightboxPrev');
  var $lightboxNext = document.getElementById('jvRvLightboxNext');

  /* ─── Search DOM refs (team only) ───────────────────────── */
  var $search = document.getElementById('jvRvSearch');
  var $searchInput = document.getElementById('jvRvSearchInput');
  var $searchResults = document.getElementById('jvRvSearchResults');
  var $searchEmpty = document.getElementById('jvRvSearchEmpty');
  var $searchLoading = document.getElementById('jvRvSearchLoading');

  /* ─── State ────────────────────────────────────────────── */
  var currentData = null;
  var currentEtag = null;
  var activeArtist = 'all';
  var pollTimer = null;
  var lightboxImages = [];
  var lightboxIndex = 0;
  var diffBlockIds = [];
  var searchDebounce = null;
  var _blobHydrated = false;
  // Set true right before a background-verify fetchFull() call that follows
  // a successful CDN-fast-path paint (2026-09-04). fetchFull() checks and
  // clears this itself, skipping showSkeleton() for that one call only, so
  // an already-painted page never flashes back to the loading skeleton.
  var _tokenCacheBackgroundVerify = false;

  /* ─── Party member name map (bride audience only) ──────────
     Built from #jv-rs-party-members embedded by Liquid.
     Keys: "Bridesmaid 1", "Mother 1", etc.
     Values: real onboarded first name.  ──────────────────── */
  var rvNameMap_ = {};
  (function() {
    // SECURITY (2026-08-28, Erin Wilson P0 incident): #jv-rs-party-members is
    // emitted from customer.metafields.custom.jv_party_members_json - the
    // LOGGED-IN customer's own party, not necessarily the bride this page is
    // showing. The Liquid guard meant to suppress it on a token mismatch
    // relies on request.query_string, which Shopify Liquid does not expose,
    // so it was inert and the island rendered for any logged-in customer
    // regardless of whose ?token= was in the URL. This silently overwrote
    // another bride's timeline/modal names with the logged-in viewer's own
    // party (Jess's own test account party members rendered on Erin Wilson's
    // runsheet, confirmed live in
    // BRIEFS/RESULTS-COWORK-erin-wilson-runsheet-view-wrong-2026-08-28.md).
    // Fix here, not just in Liquid: `audience` is already resolved above
    // (including the ?token=-forces-token-mode override at load) by the time
    // this runs, so gate consumption of the island on audience === 'bride'
    // regardless of whether it happened to render. A 'token' or 'team'
    // viewer must NEVER apply another customer's name map, full stop -
    // belt-and-braces alongside the Liquid-side fix.
    if (audience !== 'bride') return;
    var el = document.getElementById('jv-rs-party-members');
    if (!el) return;
    try {
      var blob = JSON.parse(el.textContent || el.innerHTML || '');
      rvNameMap_ = rvBuildPartyNameMap_(blob.members || []);
    } catch (e) {}
  }());

  /* ═══════════════════════════════════════════════════════════
     INAPP PDF SHARE
     Fetches PDF as blob so iOS share sheet shows "Save to Files"
     ═══════════════════════════════════════════════════════════ */

  function inAppSharePdf(pdfUrl) {
    if (!navigator.share) { window.open(pdfUrl, '_blank'); return; }
    fetch(pdfUrl)
      .then(function(res) { return res.blob(); })
      .then(function(blob) {
        var file = new File([blob], 'Runsheet.pdf', { type: 'application/pdf' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          return navigator.share({ files: [file], title: 'Runsheet' });
        }
        return navigator.share({ url: pdfUrl });
      })
      .catch(function() { window.open(pdfUrl, '_blank'); });
  }

  /* ═══════════════════════════════════════════════════════════
     BRIDGE CALLS
     ═══════════════════════════════════════════════════════════ */

  function bridgeCall(route, extra) {
    return new Promise(function(resolve, reject) {
      if (!window.jvBridge) {
        reject(new Error('Bridge not ready'));
        return;
      }

      var payload = Object.assign({}, extra || {});

      // Inject auth from jvAuth
      if (window.jvAuth) {
        payload.email = window.jvAuth.email;
        payload.shopifyCustomerTags = window.jvAuth.tags || '';
        if (window.jvAuth.role === 'bride') {
          payload.isBridalClient = true;
        }
        if (window.jvAuth.customerId) {
          payload.shopifyCustomerId = window.jvAuth.customerId;
        }
      }

      // Inject bride context from URL params (for team)
      if (audience === 'team') {
        var params = new URLSearchParams(window.location.search);
        if (params.get('bride')) payload.brideKey = params.get('bride');
        if (params.get('brideName')) payload.brideKey = params.get('brideName');
      }

      // Inject token auth + bride key from URL (no portal auth required)
      if (audience === 'token') {
        var tp = new URLSearchParams(window.location.search);
        payload.brideKey = tp.get('bride') || tp.get('brideName') || '';
        payload.key = tp.get('key') || tp.get('token') || '';
      }

      // Inject wedding date from URL (for bride direct links)
      if (audience === 'bride') {
        var bp = new URLSearchParams(window.location.search);
        if (bp.get('weddingDate')) payload.weddingDate = bp.get('weddingDate');
      }

      // TEAM AUDIENCE USES callTeam, NOT callAdmin (fixed 2026-09-20).
      //
      // This previously read `audience === 'team' ? callAdmin : call`, and the
      // reasoning above it ("admin key is always valid for team routes") was
      // true but irrelevant: the problem was never whether the admin key WORKS,
      // it was whether an artist ever HAS one. They do not.
      // snippets/jv-gas-bridge.liquid emits ADMIN_KEY only when
      // `customer.tags contains 'jv-admin'` (deliberately narrowed 2026-07-12,
      // because validateAdmin_ authorises on possession of a single shared
      // key). So for every artist this sent an EMPTY apiKey, and
      // runsheetViewer_authViewer_ (RunsheetViewerAPI.gs:394) fails closed on
      // `!apiKey` before any lookup runs -- no runsheet ever loaded, and the
      // bride search returned nothing, for anyone who is not Jess. It worked
      // for Jess alone, which is precisely why it survived: she is the only
      // person who would not have seen it fail.
      //
      // WHY callTeam IS THE RIGHT FIX AND NOT "ALSO GIVE ARTISTS THE ADMIN KEY":
      // the admin key carries no identity, only possession, so handing it to
      // every artist would re-open exactly what 2026-07-12 closed. callTeam
      // instead relies on the Worker-injected TEAM_APP_API_KEY (no admin key
      // ever reaches an artist's browser) and forwards jvAuth.email, which is
      // what shopifyBridge_validateTeam_ resolves against the _TeamOptions
      // roster -- so the caller is IDENTIFIED, not merely in possession.
      // Admins keep working unchanged: callTeam still attaches _adminKey when
      // jvAuth.role === 'admin', and validateTeam_ accepts an admin key too
      // (ShopifyBridge.gs:1009), so this is strictly additive for Jess.
      //
      // Token audience unchanged -- plain call, key validated server-side.
      var callFn = audience === 'team' ? window.jvBridge.callTeam : window.jvBridge.call;
      callFn.call(window.jvBridge, route, payload).then(resolve).catch(reject);
    });
  }

  function getRoute(suffix) {
    if (audience === 'bride') return 'bridge_bride_' + suffix;
    return 'bridge_team_' + suffix;
  }

  /* ═══════════════════════════════════════════════════════════
     INIT & FETCH
     ═══════════════════════════════════════════════════════════ */

  function init() {
    // For team audience: check if a bride is specified in URL
    if (audience === 'team') {
      var params = new URLSearchParams(window.location.search);
      var hasBride = params.get('bride') || params.get('brideName');
      if (!hasBride) {
        // No bride specified — show search UI instead of loading
        showSearch();
        bindEvents();
        return;
      }
    }

    // Bride audience — try to hydrate from the Shopify metafield blob embedded in Liquid.
    // This bypasses the GAS cold-start entirely for brides who have a synced blob.
    if (audience === 'bride') {
      var blobEl = document.getElementById('jv-runsheet-blob-data');
      if (blobEl) {
        try {
          var blob = JSON.parse(blobEl.textContent || blobEl.innerHTML);
          if (blob && blob.ok && blob.version && blob.timeline) {
            currentData = blob;
            currentEtag = blob.version.etag || null;
            _blobHydrated = true;
            render(blob);
            showContent();
            startPolling();
            checkSignoffStatus();
            fetchInspirationPhotos();
            bindEvents();
            return;
          }
        } catch (_) { /* malformed blob — fall through to GAS */ }
      }
    }

    // Bride audience, no synced blob, and Liquid says she has no published runsheet
    // (custom.runsheet_published_at blank): paint "Coming soon" immediately, no
    // skeleton, then verify quietly in the background (belt-and-braces: a published
    // bride whose metafield is missing still gets her runsheet, no one waits on it).
    if (audience === 'bride' && document.getElementById('jv-runsheet-unpublished')) {
      showEmpty();
      bindEvents();
      _quietNextFetch = true;
      waitForBridge(function() { fetchFull(); });
      return;
    }

    // Token audience — check sessionStorage before waiting for bridge.
    // Speeds up refresh within the same browser session (no customer context available).
    if (audience === 'token') {
      try {
        var tp = new URLSearchParams(window.location.search);
        // Empty key means the viewer could not be identified: do NOT read a
        // shared bucket, fall through to the authenticated GAS path instead.
        var ssKey = jvRvBlobCacheKey(tp);
        var ssRaw = ssKey ? sessionStorage.getItem(ssKey) : null;
        if (ssRaw) {
          var ssBlob = JSON.parse(ssRaw);
          if (ssBlob && ssBlob.ok && ssBlob.version && ssBlob.timeline) {
            currentData = ssBlob;
            currentEtag = ssBlob.version.etag || null;
            _blobHydrated = true;
            render(ssBlob);
            showContent();
            startPolling();
            // Token-mode brides reach the viewer from their hub "View Runsheet"
            // link (?token=), so the sign-off CTA + approved stamp + H1
            // re-approve prompt must render here too (reads the embedded blob,
            // falls back to bridge_runsheet_checkSignoff with the token).
            checkSignoffStatus();
            bindEvents();
            return;
          }
        }
      } catch (_) { /* sessionStorage unavailable or malformed, fall through */ }

      // No sessionStorage hit, this is a first-ever visit on this token in
      // this tab (the common case, a bride opening her SMS/email link).
      // Try the CDN fast path before waiting on the slow live GAS call.
      // Never blocks: a miss (network error, no cache yet, fetch throws)
      // resolves to null and falls straight through to the existing
      // waitForBridge(fetchFull) path below, unchanged.
      try {
        var fpTok = String((tp && (tp.get('token') || tp.get('key'))) || '').trim();
        if (fpTok) {
          jvRsTryCdnFastPath_(fpTok).then(function (fpBlob) {
            if (fpBlob) {
              currentData = fpBlob;
              currentEtag = fpBlob.version && fpBlob.version.etag;
              _blobHydrated = true;
              render(fpBlob);
              showContent();
              startPolling();
              checkSignoffStatus();
              // Cache to sessionStorage too, same as the live-fetch path
              // already does, so a refresh in this tab hits the faster
              // sessionStorage branch above next time.
              try {
                var fpSsKey = jvRvBlobCacheKey(tp);
                if (fpSsKey) sessionStorage.setItem(fpSsKey, JSON.stringify(fpBlob));
              } catch (_ssErr) { /* non-fatal */ }
              bindEvents();
              // Background-verify against the live GAS path so any drift
              // (a same-day edit after the cache was built) self-corrects
              // within this session, matching every other cache in this
              // family's stale-while-revalidate contract. Runs silently,
              // never re-shows the skeleton over data already on screen.
              _tokenCacheBackgroundVerify = true;
              fetchFull();
            } else {
              waitForBridge(function() { fetchFull(); });
              bindEvents();
            }
          }).catch(function () {
            waitForBridge(function() { fetchFull(); });
            bindEvents();
          });
          return;
        }
      } catch (_fpOuterErr) { /* fast path unavailable, fall through below */ }
    }

    waitForBridge(function() {
      fetchFull();
    });
    bindEvents();
  }

  function waitForBridge(cb) {
    // Token audience only needs the bridge (no portal auth)
    if (audience === 'token') {
      if (window.jvBridge) { cb(); return; }
      var ta = 0;
      var tc = setInterval(function() {
        ta++;
        if (window.jvBridge) { clearInterval(tc); cb(); }
        else if (ta > 50) { clearInterval(tc); showError('Bridge not available'); }
      }, 200);
      return;
    }
    if (window.jvBridge && window.jvAuth) { cb(); return; }
    var attempts = 0;
    var check = setInterval(function() {
      attempts++;
      if (window.jvBridge && window.jvAuth) { clearInterval(check); cb(); }
      else if (attempts > 50) { clearInterval(check); if (!_quietNextFetch) showError('Bridge not available'); }
    }, 200);
  }

  var BRIDE_LOAD_TIMEOUT_MS = 20000;
  var _fetchGen = 0;
  var _quietNextFetch = false;

  function fetchFull() {
    // 2026-09-04: skip the skeleton for exactly ONE call, the background
    // verify that follows a successful CDN-fast-path paint. Content is
    // already correctly on screen; re-showing the skeleton here would hide
    // it for no reason. Consumed (reset to false) immediately so every
    // OTHER caller of fetchFull() keeps the normal skeleton behaviour.
    var _skipSkeletonThisCall = _tokenCacheBackgroundVerify;
    _tokenCacheBackgroundVerify = false;
    // Quiet mode (2026-10-06): the background verify behind the not-published
    // "Coming soon" first paint. No skeleton, and any failure/NO_RUNSHEET leaves
    // the Coming soon state exactly as it is.
    var quiet = _quietNextFetch;
    _quietNextFetch = false;
    var myGen = ++_fetchGen;
    // Bride audience only: after ~20s stop showing an endless skeleton and say so,
    // with Retry. NO auto-retry for this read (each attempt can cost a GAS cold
    // start). The original call keeps running: if it lands late with a runsheet
    // it still renders over the "still being prepared" state.
    var preparingTimer = null;
    if (audience === 'bride' && !quiet && !_skipSkeletonThisCall) {
      preparingTimer = setTimeout(function() {
        if (myGen !== _fetchGen) return;
        showError('', 'Your runsheet is still being prepared. Tap Try Again in a moment.');
      }, BRIDE_LOAD_TIMEOUT_MS);
    }
    if (!_skipSkeletonThisCall && !quiet) showSkeleton();
    var route = audience === 'token' ? 'bridge_runsheet_tokenView'
              : audience === 'team' ? 'bridge_team_getRunsheetV2'
              : 'bridge_bride_getRunsheet';

    /* Issue #3 (2026-10-16): CF-first team-runsheet CDN cache tri-state.
       Sibling to dashboard / upcoming. Flag
       `window.JV_TEAM_RUNSHEET_CF_ENABLED`:
         'off'    -> legacy GAS path only (default)
         'shadow' -> fire the cache path in the background, log cf_ok=, serve GAS
         'on'     -> fire the cache path for a telemetry "first-paint" signal,
                     then still GAS-refresh for the full services/timeline tree.
       Only intercepts audience === 'team'; token + bride untouched. The GAS
       bridge remains the authoritative read -- the cache is a thin accelerator
       for identity + windows + people, see team-runsheet-cache.mjs header. */
    if (audience === 'team') {
      var trsMode = (typeof window.JV_TEAM_RUNSHEET_CF_ENABLED === 'string')
        ? window.JV_TEAM_RUNSHEET_CF_ENABLED : 'off';
      if (trsMode === 'shadow' || trsMode === 'on') {
        (function () {
          try {
            var params = new URLSearchParams(window.location.search);
            var brideEmail = String(params.get('bride') || params.get('brideEmail') || params.get('brideName') || '').trim();
            if (!brideEmail || brideEmail.indexOf('@') === -1) {
              try { console.info('[jv-runsheet-view CF ' + trsMode + '] skipped (no bride email in URL)'); } catch (_e) {}
              return;
            }
            var proxyBase = (window.jvBridge && window.jvBridge.appProxyBase) || '/apps/jv';
            var cfUrl = proxyBase.replace(/\/+$/, '') + '/team-runsheet/cache?bride_email=' + encodeURIComponent(brideEmail);
            var nf = (window._jvNativeFetch || window.fetch).bind(window);
            nf(cfUrl, { method: 'GET', credentials: 'same-origin' })
              .then(function (r) { return r.ok ? r.json() : { ok: false, _cfStatus: r.status }; })
              .then(function (cfRes) {
                try {
                  var cfOk = !!(cfRes && cfRes.ok && cfRes.data);
                  console.info('[jv-runsheet-view CF ' + trsMode + '] cf_ok=' + cfOk);
                } catch (_e) {}
              }).catch(function (_e) {});
          } catch (_e) {}
        }());
      }
    }

    bridgeCall(route).then(function(resp) {
      if (preparingTimer) { clearTimeout(preparingTimer); preparingTimer = null; }
      if (myGen !== _fetchGen) return; // superseded by a newer fetchFull() (e.g. Retry)
      // Quiet verify: any failure keeps the Coming soon first paint. A genuine NO_RUNSHEET
      // falls through (populates the hero with her name + date, re-shows Coming soon).
      if (quiet && (!resp || (!resp.ok && !(resp.code === 'NO_RUNSHEET' || resp.hidden)))) return;
      if (!resp || !resp.ok) {
        if (resp && (resp.code === 'NO_RUNSHEET' || resp.hidden)) {
          // Even without a runsheet, populate the hero with bride name +
          // wedding date so the "Coming soon" empty state still feels
          // personal. NO_RUNSHEET responses include resp.booking.
          if (resp.booking) populateHero_(resp.booking);
          showEmpty();
          return;
        }
        showError(resp && resp.error ? resp.error : 'Unknown error');
        return;
      }
      currentData = resp;
      currentEtag = resp.version && resp.version.etag;
      render(resp);
      showContent();
      startPolling();

      // Token audience — cache to sessionStorage so refreshes skip GAS
      if (audience === 'token') {
        try {
          var tp2 = new URLSearchParams(window.location.search);
          // Empty key means the viewer could not be identified: write nothing,
          // rather than poisoning a bucket the next bride would read.
          var ssKey2 = jvRvBlobCacheKey(tp2);
          if (ssKey2) sessionStorage.setItem(ssKey2, JSON.stringify(resp));
        } catch (_) {}
      }

      // Fetch inspiration photos async. Bride audience also checks sign-off
      // status; token (public) viewers fetch inspo via the token-authed route
      // (added 2026-06-03) so the bridal party can see the pics too.
      if (audience === 'bride') {
        checkSignoffStatus();
        fetchInspirationPhotos();
      } else if (audience === 'token') {
        // Token-mode brides arrive here from their hub "View Runsheet" link, so
        // they must see the sign-off CTA, approved stamp, and H1 re-approve
        // prompt too (token auth is consent; recordSignoff/flagForReview accept it).
        checkSignoffStatus();
        fetchInspirationPhotos();
      } else if (audience === 'team') {
        fetchRvTeamCards_();
      }
    }).catch(function(err) {
      if (preparingTimer) { clearTimeout(preparingTimer); preparingTimer = null; }
      if (myGen !== _fetchGen) return;
      if (quiet) return; // background verify: leave the Coming soon first paint alone
      showError(err.message || 'Network error');
    });
  }

  // ── Bride + party profile cards (team audience) ────────────────────────────

  /* G1 (2026-10-06, Team App lane, Jess approved): bride + party cards for the
     TEAM audience, re-enabled after ccdeac0f5 (2026-05-13) removed the old
     callAdmin() version, which 401'd for every artist. Artists read their OWN
     job via GET /team/jobs/:id (Cloudflare, jv-next-pilot): personContext is
     returned only when D1 team_job_assignments proves THIS artist is assigned
     to the wedding (jobsRepo.ts personContext EXISTS-check), and is filtered to
     artist-safe fields at write time. Nothing here trusts the browser for
     scope. Admins keep the admin-gated route. Ships DARK: no-op unless shop
     metafield custom.team_cf_runsheet_cards is true. */
  function fetchRvTeamCards_() {
    var cfg = window.JV_TEAM_CF_CONFIG || {};
    if (!(cfg.runsheetCardsEnabled === true || String(cfg.runsheetCardsEnabled) === 'true')) return;
    var panel = document.getElementById('jv-rv-cards-panel');
    if (!panel) return;
    var brideName = (currentData && currentData.booking && currentData.booking.brideName) || '';
    if (window.jvAuth && window.jvAuth.role === 'admin') { fetchRvBrideCards_(); return; }
    var api = window.jvTeamApi;
    if (!api || typeof api.getJob !== 'function') return;
    var jobId = rvResolveJobId_(brideName);
    if (!jobId) return;
    var cached = api.cachedJob && api.cachedJob(jobId);
    if (cached && cached.payload) rvRenderTeamCards_(panel, cached.payload, brideName); // local-first
    api.getJob(jobId).then(function(job) {
      rvRenderTeamCards_(panel, job, brideName);
    }).catch(function() { /* not assigned / offline: leave cached or empty panel */ });
  }

  function rvResolveJobId_(brideName) {
    try {
      var q = new URLSearchParams(window.location.search).get('job');
      if (q && /^\d+$/.test(q)) return q;
    } catch (e) {}
    var want = String(brideName || '').trim().toLowerCase();
    var list = _rvBrideList_ || [];
    var hits = list.filter(function(b) { return b.jobId && String(b.name || '').trim().toLowerCase() === want; });
    return hits.length === 1 ? hits[0].jobId : ''; // ambiguous name -> no guess
  }

  function rvRenderTeamCards_(panel, job, brideName) {
    var people = (job && Array.isArray(job.personContext)) ? job.personContext : [];
    if (!people.length) return;
    var b = null, members = [];
    people.forEach(function(p) {
      if (p.kind === 'bride' && !b) b = p;
      else if (p.kind === 'member') members.push({
        name: p.displayName || '', role: p.roleLabel || '', profile_pic_url: p.profilePicUrl || '',
        hair_style: p.hairStylePref || '', allergies: p.allergies || '', skin_concerns: p.skinConcerns || '',
        colour_palette: p.colourPalette || '', inspo_notes: p.inspoNotes || ''
      });
    });
    var bride = {
      profile: b ? { profilePicUrl: b.profilePicUrl || '', beautyNotes: b.beautyNotes || '' } : {},
      brief: b ? { hairstylePref: b.hairStylePref || '', makeupVibe: b.makeupVibePref || '' } : {},
      inspo: {}
    };
    var name = brideName || (b && b.displayName) || '';
    var html = '<div class="jv-pc-section" style="padding:1.5rem 0 0.5rem;">';
    html += '<p class="jv-pc-section__heading">Vision &amp; Party</p>';
    html += rvPcRenderCards_(bride, members, name);
    html += '</div>';
    panel.innerHTML = html;
    rvPcInitCards_(panel, bride, members, name);
  }

  function fetchRvBrideCards_() {
    var email = currentData && currentData.profile && currentData.profile.email;
    if (!email || !window.jvBridge) return;
    var panel = document.getElementById('jv-rv-cards-panel');
    if (!panel) return;
    var brideName = (currentData && currentData.booking && currentData.booking.brideName) || '';

    window.jvBridge.callAdmin('bridge_admin_getBrideCardsData', { brideEmail: email })
      .then(function(res) {
        if (!res || !res.ok) return;
        var bride = res.bride || {};
        var members = res.partyMembers || [];
        var hasContent = bride.profile || bride.brief || bride.inspo || members.length;
        if (!hasContent) return;

        var html = '<div class="jv-pc-section" style="padding:1.5rem 0 0.5rem;">';
        html += '<p class="jv-pc-section__heading">Vision &amp; Party</p>';
        html += rvPcRenderCards_(bride, members, brideName);
        html += '</div>';
        panel.innerHTML = html;
        rvPcInitCards_(panel, bride, members, brideName);
      })
      .catch(function() {});
  }

  function rvPcEsc_(s) {
    return String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
  }

  function rvPcThumbsHtml_(hairUrls, makeupUrls, max) {
    var hairIds = Array.isArray(hairUrls) ? hairUrls : (hairUrls ? String(hairUrls).split(',') : []);
    var makeupIds = Array.isArray(makeupUrls) ? makeupUrls : (makeupUrls ? String(makeupUrls).split(',') : []);
    var all = hairIds.concat(makeupIds).map(function(f){return f.trim();}).filter(Boolean).slice(0, max || 4);
    if (!all.length) return '';
    return '<div class="jv-pc-thumb-strip">' + all.map(function(fid) {
      var url = 'https://lh3.googleusercontent.com/d/' + fid;
      return '<a href="' + rvPcEsc_(url) + '" target="_blank" rel="noopener" class="jv-party-hub__thumb-link"><img src="' + rvPcEsc_(url) + '" class="jv-party-hub__thumb" loading="lazy" alt="Inspiration"></a>';
    }).join('') + '</div>';
  }

  function rvPcRenderCards_(bride, partyMembers, brideName) {
    var profile = bride.profile || {};
    var inspo = bride.inspo || {};
    var initials = (brideName || 'B').split(' ').map(function(p){return p.charAt(0);}).join('').slice(0,2).toUpperCase() || 'B';
    var picUrl = profile.profilePicUrl || '';
    var hairIds = inspo.hair || [];
    var makeupIds = inspo.makeup || [];
    var brideThumbsHtml = rvPcThumbsHtml_(hairIds, makeupIds, 4);

    var html = '<div class="jv-party-hub__card jv-rv-pc-card" data-jv-rv-pc-type="bride">';
    html += '<div class="jv-party-hub__card-header">';
    html += '<div class="jv-party-hub__avatar">' + (picUrl ? '<img class="jv-party-hub__avatar-img" src="' + rvPcEsc_(picUrl) + '" alt="' + rvPcEsc_(brideName) + '" loading="lazy">' : rvPcEsc_(initials)) + '</div>';
    html += '<div class="jv-party-hub__card-identity"><p class="jv-party-hub__card-name">' + rvPcEsc_(brideName || 'Bride') + '</p><p class="jv-party-hub__card-role">Bride</p></div>';
    html += '<div class="jv-party-hub__card-meta">' + brideThumbsHtml + '<button type="button" class="jv-party-hub__expand-btn jv-rv-pc-view-full" data-jv-rv-pc-type="bride">View full</button></div>';
    html += '</div></div>';

    if (partyMembers.length) {
      html += '<div class="jv-party-hub__grid" style="margin-top:0.75rem;">';
      partyMembers.forEach(function(m, idx) {
        var name = m.name || 'Party Member';
        var role = m.role || '';
        var mPic = m.profile_pic_url || '';
        var mInitials = name.split(' ').map(function(p){return p.charAt(0);}).join('').slice(0,2).toUpperCase() || '?';
        var mThumbs = rvPcThumbsHtml_(m.hair_inspo_urls, m.makeup_inspo_urls, 4);
        html += '<div class="jv-party-hub__card jv-rv-pc-card" data-jv-rv-pc-type="member" data-jv-rv-pc-idx="' + idx + '">';
        html += '<div class="jv-party-hub__card-header">';
        html += '<div class="jv-party-hub__avatar">' + (mPic ? '<img class="jv-party-hub__avatar-img" src="' + rvPcEsc_(mPic) + '" alt="' + rvPcEsc_(name) + '" loading="lazy">' : rvPcEsc_(mInitials)) + '</div>';
        html += '<div class="jv-party-hub__card-identity"><p class="jv-party-hub__card-name">' + rvPcEsc_(name) + '</p>' + (role ? '<p class="jv-party-hub__card-role">' + rvPcEsc_(role) + '</p>' : '') + '</div>';
        html += '<div class="jv-party-hub__card-meta">' + mThumbs + '<button type="button" class="jv-party-hub__expand-btn jv-rv-pc-view-full" data-jv-rv-pc-type="member" data-jv-rv-pc-idx="' + idx + '">View full</button></div>';
        html += '</div></div>';
      });
      html += '</div>';
    }
    return html;
  }

  function rvPcModalBodyForBride_(bride, brideName) {
    var profile = bride.profile || {};
    var brief = bride.brief || {};
    var inspo = bride.inspo || {};
    var html = '<p class="jv-pc-modal__name">' + rvPcEsc_(brideName || 'Bride') + '</p><p class="jv-pc-modal__role">Bride</p>';
    var words = profile.visionWords || [];
    if (words.length) {
      html += '<p class="jv-party-hub__sub-heading">Wedding Vision</p><div style="display:flex;flex-wrap:wrap;gap:0.4rem;margin-bottom:0.75rem;">';
      words.forEach(function(w) { html += '<span class="jv-vision-word-badge">' + rvPcEsc_(w) + '</span>'; });
      html += '</div>';
    }
    html += '<p class="jv-party-hub__sub-heading">Details</p><div class="jv-party-hub__detail-grid">';
    [['Partner', profile.partnerName], ['Beauty notes', profile.beautyNotes], ['Hairstyle', brief.hairstylePref], ['Makeup vibe', brief.makeupVibe]].forEach(function(r) {
      if (!r[1]) return;
      html += '<div class="jv-party-hub__detail-row"><span class="jv-party-hub__detail-label">' + rvPcEsc_(r[0]) + '</span><span class="jv-party-hub__detail-value">' + rvPcEsc_(r[1]).replace(/\n/g,'<br>') + '</span></div>';
    });
    html += '</div>';
    var hairIds = inspo.hair || [];
    var makeupIds = inspo.makeup || [];
    if (hairIds.length) {
      html += '<p class="jv-party-hub__sub-heading">Hair Inspiration</p><div class="jv-pc-modal__inspo-grid">';
      hairIds.forEach(function(fid) { var u = 'https://lh3.googleusercontent.com/d/' + rvPcEsc_(fid); html += '<a href="' + u + '" target="_blank" rel="noopener"><img src="' + u + '" loading="lazy" alt="Hair inspiration"></a>'; });
      html += '</div>';
    }
    if (makeupIds.length) {
      html += '<p class="jv-party-hub__sub-heading">Makeup Inspiration</p><div class="jv-pc-modal__inspo-grid">';
      makeupIds.forEach(function(fid) { var u = 'https://lh3.googleusercontent.com/d/' + rvPcEsc_(fid); html += '<a href="' + u + '" target="_blank" rel="noopener"><img src="' + u + '" loading="lazy" alt="Makeup inspiration"></a>'; });
      html += '</div>';
    }
    return html;
  }

  function rvPcModalBodyForMember_(m) {
    var name = m.name || 'Party Member';
    var html = '<p class="jv-pc-modal__name">' + rvPcEsc_(name) + '</p>' + (m.role ? '<p class="jv-pc-modal__role">' + rvPcEsc_(m.role) + '</p>' : '');
    var fields = [['Hairstyle', m.hair_style], ['Colour palette', m.colour_palette], ['Skin concerns', m.skin_concerns], ['Allergies', m.allergies], ['Notes', m.inspo_notes]];
    var hasDetails = fields.some(function(f) { return !!f[1]; });
    if (hasDetails) {
      html += '<p class="jv-party-hub__sub-heading">Details</p><div class="jv-party-hub__detail-grid">';
      fields.forEach(function(r) {
        if (!r[1]) return;
        html += '<div class="jv-party-hub__detail-row"><span class="jv-party-hub__detail-label">' + rvPcEsc_(r[0]) + '</span><span class="jv-party-hub__detail-value">' + rvPcEsc_(r[1]).replace(/\n/g,'<br>') + '</span></div>';
      });
      html += '</div>';
    }
    var hairIds = Array.isArray(m.hair_inspo_urls) ? m.hair_inspo_urls : (m.hair_inspo_urls ? String(m.hair_inspo_urls).split(',') : []);
    var makeupIds = Array.isArray(m.makeup_inspo_urls) ? m.makeup_inspo_urls : (m.makeup_inspo_urls ? String(m.makeup_inspo_urls).split(',') : []);
    hairIds = hairIds.map(function(f){return f.trim();}).filter(Boolean);
    makeupIds = makeupIds.map(function(f){return f.trim();}).filter(Boolean);
    if (hairIds.length) {
      html += '<p class="jv-party-hub__sub-heading">Hair Inspiration</p><div class="jv-pc-modal__inspo-grid">';
      hairIds.forEach(function(fid) { var u = 'https://lh3.googleusercontent.com/d/' + fid; html += '<a href="' + rvPcEsc_(u) + '" target="_blank" rel="noopener"><img src="' + rvPcEsc_(u) + '" loading="lazy" alt="Hair inspiration"></a>'; });
      html += '</div>';
    }
    if (makeupIds.length) {
      html += '<p class="jv-party-hub__sub-heading">Makeup Inspiration</p><div class="jv-pc-modal__inspo-grid">';
      makeupIds.forEach(function(fid) { var u = 'https://lh3.googleusercontent.com/d/' + fid; html += '<a href="' + rvPcEsc_(u) + '" target="_blank" rel="noopener"><img src="' + rvPcEsc_(u) + '" loading="lazy" alt="Makeup inspiration"></a>'; });
      html += '</div>';
    }
    return html;
  }

  var _rvPcCardData_ = null;

  function rvPcInitCards_(container, bride, partyMembers, brideName) {
    _rvPcCardData_ = { bride: bride, partyMembers: partyMembers, brideName: brideName };
    if (!document.getElementById('jv-rv-pc-modal')) {
      var backdrop = document.createElement('div');
      backdrop.className = 'jv-pc-modal-backdrop';
      backdrop.id = 'jv-rv-pc-modal';
      backdrop.style.display = 'none';
      backdrop.innerHTML = '<div class="jv-pc-modal"><button class="jv-pc-modal__close" id="jv-rv-pc-modal-close" aria-label="Close">&times;</button><div class="jv-pc-modal__body" id="jv-rv-pc-modal-body"></div></div>';
      document.body.appendChild(backdrop);
      document.getElementById('jv-rv-pc-modal-close').addEventListener('click', rvPcCloseModal_);
      backdrop.addEventListener('click', function(e) { if (e.target === backdrop) rvPcCloseModal_(); });
      document.addEventListener('keydown', function(e) { if (e.key === 'Escape') rvPcCloseModal_(); });
    }
    container.querySelectorAll('.jv-rv-pc-view-full').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var type = btn.getAttribute('data-jv-rv-pc-type');
        var bodyEl = document.getElementById('jv-rv-pc-modal-body');
        if (!bodyEl || !_rvPcCardData_) return;
        var d = _rvPcCardData_;
        bodyEl.innerHTML = type === 'bride'
          ? rvPcModalBodyForBride_(d.bride, d.brideName)
          : rvPcModalBodyForMember_((d.partyMembers || [])[parseInt(btn.getAttribute('data-jv-rv-pc-idx') || '0', 10)] || {});
        document.getElementById('jv-rv-pc-modal').style.display = 'flex';
        document.body.style.overflow = 'hidden';
      });
    });
  }

  function rvPcCloseModal_() {
    var m = document.getElementById('jv-rv-pc-modal');
    if (m) m.style.display = 'none';
    document.body.style.overflow = '';
  }

  // ── Inspiration photos (bride audience) ─────────────────────────────────────

  // Fetches Drive inspiration thumbnails after the timeline is visible.
  // Patches currentData.inspiration.byPerson when complete — no re-render needed
  // since cards are built on modal open, not at initial render.
  function fetchInspirationPhotos() {
    if (!currentData || !currentData.inspiration || currentData.inspiration.mode !== 'deferred') return;
    // Token (public/logged-out) viewers must use the token-authed inspo route;
    // the bride route requires a logged-in customer. bridgeCall injects the token
    // key for token audience. 2026-06-03.
    var inspoRoute = audience === 'token' ? 'bridge_runsheet_tokenViewInspo' : 'bridge_bride_getInspirationPhotos';
    bridgeCall(inspoRoute).then(function(resp) {
      if (!resp || !resp.ok || !resp.inspiration) return;
      if (currentData && currentData.inspiration) {
        currentData.inspiration.byPerson = resp.inspiration.byPerson || {};
        currentData.inspiration.mode = resp.inspiration.mode || 'inline';
      }
    }).catch(function() { /* non-fatal — folder link still shows */ });
  }

  function checkSignoffStatus() {
    // EBM brides get no sign-off and no change-request control (Jess,
    // 2026-07-28). Belt-and-braces: the Liquid already omits both blocks for
    // an EBM bride, and this JS guard returns before anything (wireInlineSignoff_,
    // _renderInlineSignoff) can reveal them, in case cached HTML still carries
    // the markup. The read-only timeline is unaffected.
    if (jvRvIsEbmBride_()) return;

    // Wire the inline sign-off button + modal on every status check (handles
    // both blob-hydrate and GAS-fetch paths).
    wireInlineSignoff_();

    // Path A Migration 3: try CDN-cached metafield blob first (~0ms). Falls
    // back to bridge_runsheet_checkSignoff (auth-agnostic: token OR email).
    // Phase 0 (2026-06-17): treat signedOff:false as also terminal; render
    // the CTA immediately from the blob instead of falling through to GAS.
    // Phase 3c (2026-06-27): also read change-note blob from CDN.
    //
    // SECURITY (2026-08-28, Stephanie Garland P0-2): these islands are
    // customer.metafields for WHOEVER IS LOGGED IN, not necessarily the
    // bride this page is showing (the Liquid-side guard cannot reliably
    // detect a ?token= mismatch on this template - see the matching comment
    // in sections/jv-runsheet-view.liquid). This block had no audience gate
    // at all, so a logged-in viewer's OWN prior sign-off could render as a
    // false "approved" banner on a different bride's runsheet (confirmed
    // live on Stephanie Garland's page). Only trust these CDN islands for
    // audience === 'bride' - a token/team viewer always falls through to the
    // live GAS lookup below, which resolves the correct bride by token
    // server-side.
    var changeNote = null;
    if (audience === 'bride') {
      try {
        var cnEl = document.getElementById('jv-rs-change-note');
        if (cnEl && cnEl.textContent) {
          var cnParsed = JSON.parse(cnEl.textContent);
          if (cnParsed && cnParsed.note) changeNote = String(cnParsed.note);
        }
      } catch (cnErr) { /* non-fatal */ }

      try {
        var blobEl = document.getElementById('jv-rs-signoff-status');
        if (blobEl && blobEl.textContent) {
          var parsed = JSON.parse(blobEl.textContent);
          if (parsed && typeof parsed.signedOff !== 'undefined') {
            var reapprove = parsed.reapprovalRequired === true;
            renderSignoffBanner(parsed.signedOff, parsed.signoffDate || null, reapprove, changeNote);
            _renderInlineSignoff(parsed.signedOff, parsed.signoffDate || null, reapprove);
            return;
          }
        }
      } catch (e) { /* fall through to GAS */ }
    }

    // Auth-agnostic — passes token in payload so token-mode brides also work.
    var extra = {};
    try {
      var sp = new URLSearchParams(window.location.search);
      var tok = (sp.get('token') || sp.get('key') || '').trim();
      if (tok) extra.token = tok;
    } catch (e) { /* ignore */ }

    bridgeCall('bridge_runsheet_checkSignoff', extra).then(function(resp) {
      if (!resp || !resp.ok) {
        // Not signed off yet — show the inline prompt so the bride can act.
        _renderInlineSignoff(false, null);
        return;
      }
      renderSignoffBanner(resp.signedOff, resp.signoffDate, resp.reapprovalRequired === true, changeNote);
      _renderInlineSignoff(resp.signedOff, resp.signoffDate, resp.reapprovalRequired === true);
    }).catch(function() {
      _renderInlineSignoff(false, null);
    });
  }

  // Phase 3c — changeNote: optional string from custom.runsheet_change_note_json.
  // When present and the bride is signed off, renders an informational note block
  // below the green approval stamp (no re-sign required).
  function renderSignoffBanner(signedOff, signoffDate, reapprovalRequired, changeNote) {
    var existing = document.getElementById('jvRvSignoffBanner');
    if (existing) existing.remove();

    var banner = document.createElement('div');
    banner.id = 'jvRvSignoffBanner';

    if (signedOff) {
      banner.className = 'jv-rv__signoff-banner jv-rv__signoff-banner--done';
      var icon = '<svg viewBox="0 0 24 24" width="16" height="16" style="flex-shrink:0"><polyline points="20 6 9 17 4 12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>';
      var dateStr = signoffDate ? ' on ' + new Date(signoffDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
      var noteHtml = '';
      if (changeNote) {
        // Informational note: the runsheet was updated after she signed off but
        // the admin did not require re-approval. Shown below the green stamp.
        noteHtml = '<div class="jv-rv__signoff-banner-note">'
          + '<strong>A small update was made to your runsheet:</strong> '
          + changeNote.replace(/</g, '&lt;').replace(/>/g, '&gt;')
          + '</div>';
      }
      banner.innerHTML = icon + '<span>You signed off your runsheet' + dateStr + '</span>' + noteHtml;
    } else if (reapprovalRequired) {
      // H1: plan changed since she approved. Re-ask, do not show the green stamp.
      banner.className = 'jv-rv__signoff-banner jv-rv__signoff-banner--todo';
      banner.innerHTML = '<span>This runsheet was updated since you approved it. Please review the changes and approve again.</span>' +
        '<button type="button" class="jv-rv__signoff-banner-btn" id="jvRvSignoffBannerCta">Review &amp; re-approve</button>';
    } else {
      banner.className = 'jv-rv__signoff-banner jv-rv__signoff-banner--todo';
      banner.innerHTML = '<span>Please review and sign off your runsheet</span>' +
        '<button type="button" class="jv-rv__signoff-banner-btn" id="jvRvSignoffBannerCta">Sign off</button>';
    }

    // Insert immediately after the header
    var header = document.getElementById('jvRvHeader');
    if (header && header.parentNode) {
      header.parentNode.insertBefore(banner, header.nextSibling);
    }

    // Wire the top-banner CTA to the same modal as the bottom button.
    var bannerBtn = document.getElementById('jvRvSignoffBannerCta');
    if (bannerBtn) bannerBtn.addEventListener('click', _openSignoffModal);
  }

  /* ─── Inline sign-off (bottom block + confirmation modal) ─────────── */

  function _renderInlineSignoff(signedOff, signoffDate, reapprovalRequired) {
    var block = document.getElementById('jvRvSignoffBlock');
    var prompt = document.getElementById('jvRvSignoffPrompt');
    var done = document.getElementById('jvRvSignoffDone');
    var doneText = document.getElementById('jvRvSignoffDoneText');
    if (!block || !prompt || !done) return;

    block.style.display = '';
    if (signedOff) {
      prompt.style.display = 'none';
      done.style.display = '';
      var dateStr = signoffDate ? ' on ' + new Date(signoffDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
      if (doneText) doneText.textContent = 'Runsheet approved' + dateStr;
    } else {
      // Not signed off (or reapproval required after a changed re-publish).
      prompt.style.display = '';
      done.style.display = 'none';
      var promptText = prompt.querySelector('p');
      if (promptText) {
        promptText.textContent = reapprovalRequired
          ? 'This runsheet has been updated since you approved it. Please review the changes and approve again.'
          : 'Happy with your runsheet? Approve it below and we\'ll lock it in.';
      }
      var ctaBtn = document.getElementById('jvRvSignoffCta');
      if (ctaBtn) ctaBtn.textContent = reapprovalRequired ? 'Review & re-approve' : 'Sign off & approve runsheet';
    }

    // Request-a-change is always available once the runsheet is published,
    // independent of sign-off state.
    var amendBlock = document.getElementById('jvRvAmendBlock');
    if (amendBlock) amendBlock.style.display = '';
  }

  function wireInlineSignoff_() {
    var block = document.getElementById('jvRvSignoffBlock');
    if (block) block.style.display = '';

    var cta = document.getElementById('jvRvSignoffCta');
    if (cta && !cta.dataset.bound) {
      cta.addEventListener('click', _openSignoffModal);
      cta.dataset.bound = '1';
    }

    var backdrop = document.getElementById('jvRvSignoffModalBackdrop');
    var cancel = document.getElementById('jvRvSignoffModalCancel');
    var confirm = document.getElementById('jvRvSignoffModalConfirm');
    if (cancel && !cancel.dataset.bound)   { cancel.addEventListener('click', _closeSignoffModal); cancel.dataset.bound = '1'; }
    if (backdrop && !backdrop.dataset.bound){ backdrop.addEventListener('click', _closeSignoffModal); backdrop.dataset.bound = '1'; }
    if (confirm && !confirm.dataset.bound) { confirm.addEventListener('click', _submitSignoff); confirm.dataset.bound = '1'; }

    // H2: change-request flow inside the same modal.
    var crOpen  = document.getElementById('jvRvChangeRequestOpen');
    var crBack  = document.getElementById('jvRvChangeRequestBack');
    var crSend  = document.getElementById('jvRvChangeRequestSend');
    var crClose = document.getElementById('jvRvChangeRequestClose');
    if (crOpen  && !crOpen.dataset.bound)  { crOpen.addEventListener('click', _openChangeRequest); crOpen.dataset.bound = '1'; }
    if (crBack  && !crBack.dataset.bound)  { crBack.addEventListener('click', _showApproveView_); crBack.dataset.bound = '1'; }
    if (crSend  && !crSend.dataset.bound)  { crSend.addEventListener('click', _submitChangeRequest); crSend.dataset.bound = '1'; }

    wireAmendModal_();
    if (crClose && !crClose.dataset.bound) { crClose.addEventListener('click', _closeSignoffModal); crClose.dataset.bound = '1'; }

    wireShareBtn_();
  }

  /* Change request (H2). A clearly-secondary path next to "approve" so a bride
     can flag a problem instead of approving a plan that looks wrong. Writes a
     tracked change_requested record and pings Jess on Chat via
     bridge_runsheet_flagForReview. */

  function _showApproveView_() {
    var a = document.getElementById('jvRvSignoffApproveView');
    var c = document.getElementById('jvRvChangeRequestView');
    var d = document.getElementById('jvRvChangeRequestDone');
    if (a) a.style.display = '';
    if (c) c.style.display = 'none';
    if (d) d.style.display = 'none';
  }

  function _openChangeRequest() {
    var a = document.getElementById('jvRvSignoffApproveView');
    var c = document.getElementById('jvRvChangeRequestView');
    var err = document.getElementById('jvRvChangeRequestError');
    if (err) { err.style.display = 'none'; err.textContent = ''; }
    if (a) a.style.display = 'none';
    if (c) c.style.display = '';
  }

  function _submitChangeRequest() {
    var send = document.getElementById('jvRvChangeRequestSend');
    var note = document.getElementById('jvRvChangeRequestNote');
    var err = document.getElementById('jvRvChangeRequestError');
    if (err) { err.style.display = 'none'; err.textContent = ''; }

    var noteVal = note ? String(note.value || '').trim() : '';
    if (!noteVal) {
      if (err) { err.textContent = 'Please add a quick note about what needs changing.'; err.style.display = ''; }
      return;
    }
    if (send) { send.disabled = true; send.textContent = 'Sending…'; }

    var extra = { note: noteVal };
    try {
      var sp = new URLSearchParams(window.location.search);
      var tok = (sp.get('token') || sp.get('key') || '').trim();
      if (tok) extra.token = tok;
    } catch (e) { /* ignore */ }

    bridgeCall('bridge_runsheet_flagForReview', extra).then(function(resp) {
      if (!resp || !resp.ok) {
        var msg = (resp && resp.error) || 'Could not send your change request. Please try again.';
        if (err) { err.textContent = msg; err.style.display = ''; }
        if (send) { send.disabled = false; send.textContent = 'Send change request'; }
        return;
      }
      // Swap to the calm confirmation (no green "approved" stamp).
      var c = document.getElementById('jvRvChangeRequestView');
      var d = document.getElementById('jvRvChangeRequestDone');
      if (c) c.style.display = 'none';
      if (d) d.style.display = '';
    }).catch(function(e) {
      if (err) { err.textContent = (e && e.message) || 'Network error. Please try again.'; err.style.display = ''; }
      if (send) { send.disabled = false; send.textContent = 'Send change request'; }
    });
  }

  /* ─── Request a change (Admin Bride Profile brief, 2026-07-02) ───────
     Always-available, categorised. Writes RUNSHEET_AMENDMENT_REQUESTS_JSON
     via bridge_bride_requestRunsheetAmendment. Separate from the free-text
     jvRvChangeRequest* flow above (tied to sign-off, no category field). */

  function _openAmendModal_() {
    var modal = document.getElementById('jvRvAmendModal');
    var note = document.getElementById('jvRvAmendNote');
    var err = document.getElementById('jvRvAmendError');
    var confirm = document.getElementById('jvRvAmendConfirm');
    if (note) note.value = '';
    if (err) { err.style.display = 'none'; err.textContent = ''; }
    if (confirm) confirm.style.display = 'none';
    if (modal) modal.style.display = 'flex';
  }

  function _closeAmendModal_() {
    var modal = document.getElementById('jvRvAmendModal');
    if (modal) modal.style.display = 'none';
  }

  function _submitAmendRequest_() {
    var send = document.getElementById('jvRvAmendSend');
    var note = document.getElementById('jvRvAmendNote');
    var err = document.getElementById('jvRvAmendError');
    var typeInput = document.querySelector('input[name="jvRvAmendType"]:checked');
    if (err) { err.style.display = 'none'; err.textContent = ''; }

    var noteVal = note ? String(note.value || '').trim() : '';
    if (!noteVal) {
      if (err) { err.textContent = 'Please add a note about what you would like changed.'; err.style.display = ''; }
      return;
    }
    if (send) { send.disabled = true; send.textContent = 'Submitting…'; }

    var extra = { note: noteVal, requestType: typeInput ? typeInput.value : 'other' };
    try {
      var sp = new URLSearchParams(window.location.search);
      var tok = (sp.get('token') || sp.get('key') || '').trim();
      if (tok) extra.token = tok;
    } catch (e) { /* ignore */ }

    bridgeCall('bridge_bride_requestRunsheetAmendment', extra).then(function(resp) {
      if (!resp || !resp.ok) {
        var msg = (resp && resp.error) || 'Could not submit request. Please try again.';
        if (err) { err.textContent = msg; err.style.display = ''; }
        if (send) { send.disabled = false; send.textContent = 'Submit request'; }
        return;
      }
      _closeAmendModal_();
      var confirm = document.getElementById('jvRvAmendConfirm');
      if (confirm) confirm.style.display = '';
      if (send) { send.disabled = false; send.textContent = 'Submit request'; }
    }).catch(function(e) {
      if (err) { err.textContent = (e && e.message) || 'Could not submit request. Please try again.'; err.style.display = ''; }
      if (send) { send.disabled = false; send.textContent = 'Submit request'; }
    });
  }

  function wireAmendModal_() {
    var openBtn = document.getElementById('jvRvAmendOpen');
    var cancelBtn = document.getElementById('jvRvAmendCancel');
    var sendBtn = document.getElementById('jvRvAmendSend');
    var backdrop = document.getElementById('jvRvAmendModalBackdrop');
    if (openBtn && !openBtn.dataset.bound) { openBtn.addEventListener('click', _openAmendModal_); openBtn.dataset.bound = '1'; }
    if (cancelBtn && !cancelBtn.dataset.bound) { cancelBtn.addEventListener('click', _closeAmendModal_); cancelBtn.dataset.bound = '1'; }
    if (sendBtn && !sendBtn.dataset.bound) { sendBtn.addEventListener('click', _submitAmendRequest_); sendBtn.dataset.bound = '1'; }
    if (backdrop && !backdrop.dataset.bound) { backdrop.addEventListener('click', _closeAmendModal_); backdrop.dataset.bound = '1'; }
  }

  /* ─── Share runsheet link ────────────────────────────────────────────
     Copies the public token-viewer URL to the clipboard so the bride can
     forward her runsheet to her bridal party / partner / family without
     anyone needing to log in. Token source preference order:
       1. ?token=<UUID> already in the URL (token mode + admin Copy Link)
       2. window.jvRvShareToken — embedded by Liquid for logged-in brides
          from customer.metafields.custom.runsheet_viewer_token
       3. None → button stays disabled (silent — no token, no share)
  ──────────────────────────────────────────────────────────────────── */

  function _getShareUrl_() {
    var token = '';
    try {
      var sp = new URLSearchParams(window.location.search);
      token = (sp.get('token') || sp.get('key') || '').trim();
    } catch (e) { /* ignore */ }
    if (!token && typeof window.jvRvShareToken === 'string') {
      token = String(window.jvRvShareToken).trim();
    }
    if (!token) return '';
    return 'https://jessicavegas.com/pages/runsheet-view?token=' + encodeURIComponent(token);
  }

  function wireShareBtn_() {
    var btn = document.getElementById('jvRvShareBtn');
    if (!btn || btn.dataset.bound) return;
    var label = document.getElementById('jvRvShareBtnLabel');
    var shareUrl = _getShareUrl_();
    if (!shareUrl) {
      // No token available — hide the button entirely rather than show a
      // broken-feeling disabled state.
      btn.style.display = 'none';
      return;
    }
    btn.dataset.bound = '1';
    btn.addEventListener('click', function () {
      var done = function (ok) {
        if (!label) return;
        var prev = label.textContent;
        label.textContent = ok ? 'Link copied!' : 'Copy failed — try again';
        btn.classList.add('jv-rv__footer-btn--flash');
        setTimeout(function () {
          label.textContent = prev;
          btn.classList.remove('jv-rv__footer-btn--flash');
        }, 2000);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(shareUrl).then(function () { done(true); }, function () {
          // Permissions denied or HTTPS-only API blocked — surface the URL.
          window.prompt('Copy this link to share:', shareUrl);
          done(true);
        });
      } else {
        // Legacy browser — fall back to prompt().
        window.prompt('Copy this link to share:', shareUrl);
        done(true);
      }
    });
  }

  function _openSignoffModal() {
    var modal = document.getElementById('jvRvSignoffModal');
    var err = document.getElementById('jvRvSignoffModalError');
    var brideLine = document.getElementById('jvRvSignoffModalBride');
    // Always open on the approve view (a prior change-request session may have left it switched).
    if (typeof _showApproveView_ === 'function') _showApproveView_();
    if (err) { err.style.display = 'none'; err.textContent = ''; }
    // Show whose runsheet is being approved — prevents accidental sign-off
    // when the URL is forwarded to a partner/family/admin.
    if (brideLine) {
      var brideName = currentData && currentData.booking && currentData.booking.brideName;
      if (brideName) {
        brideLine.textContent = "You're approving " + brideName + "'s runsheet";
        brideLine.style.display = '';
      } else {
        brideLine.style.display = 'none';
      }
    }
    if (modal) modal.style.display = 'flex';
  }

  function _closeSignoffModal() {
    var modal = document.getElementById('jvRvSignoffModal');
    if (modal) modal.style.display = 'none';
  }

  function _submitSignoff() {
    var confirm = document.getElementById('jvRvSignoffModalConfirm');
    var cta = document.getElementById('jvRvSignoffCta');
    var err = document.getElementById('jvRvSignoffModalError');
    if (err) { err.style.display = 'none'; err.textContent = ''; }
    if (confirm) { confirm.disabled = true; confirm.textContent = 'Approving…'; }
    if (cta) cta.disabled = true;

    var extra = {};
    try {
      var sp = new URLSearchParams(window.location.search);
      var tok = (sp.get('token') || sp.get('key') || '').trim();
      if (tok) extra.token = tok;
    } catch (e) { /* ignore */ }

    bridgeCall('bridge_runsheet_recordSignoff', extra).then(function(resp) {
      if (!resp || !resp.ok) {
        // IDENTITY_MISMATCH → a different bride is logged in. Force-logout
        // and re-login so the session matches the token's bride.
        var code = resp && resp.code;
        var msg = (resp && resp.error) || 'Could not record sign-off. Please try again.';
        if (err) {
          if (code === 'IDENTITY_MISMATCH') {
            var returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
            var loginHref = '/account/logout?return_url=' + encodeURIComponent('/account/login?return_url=' + returnUrl);
            var safeMsg = String(msg).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            err.innerHTML =
              '<span style="display:block;margin-bottom:0.55rem;">' + safeMsg + '</span>' +
              '<a href="' + loginHref + '" style="display:inline-block;background:var(--brand-primary,#c99591);color:#fff;border-radius:999px;padding:0.5rem 1.1rem;text-decoration:none;font-weight:600;">Log out and try again</a>';
            err.style.display = '';
          } else {
            err.textContent = msg;
            err.style.display = '';
          }
        }
        if (confirm) { confirm.disabled = false; confirm.textContent = 'Yes, approve it'; }
        if (cta) cta.disabled = false;
        return;
      }
      _closeSignoffModal();
      renderSignoffBanner(true, resp.signoffDate);
      _renderInlineSignoff(true, resp.signoffDate);
    }).catch(function(e) {
      if (err) { err.textContent = (e && e.message) || 'Network error. Please try again.'; err.style.display = ''; }
      if (confirm) { confirm.disabled = false; confirm.textContent = 'Yes, approve it'; }
      if (cta) cta.disabled = false;
    });
  }

  /* ─── Artist avatar lookup helper ─────────────────────────── */
  function artistAvatarByName_(name) {
    if (!currentData || !currentData.artists || !name) return '';
    for (var i = 0; i < currentData.artists.length; i++) {
      if (currentData.artists[i].name === name) return currentData.artists[i].avatarUrl || '';
    }
    return '';
  }

  /* ─── Party member name resolution helpers ─────────────────
     Maps "Bridesmaid 1" → onboarded member name from blob.    */
  function rvNormalizeRole_(raw) {
    var r = String(raw || '').toLowerCase().trim();
    if (r === 'bridesmaid' || r === 'maid of honour' || r === 'maid of honor' || r === 'moh') return 'Bridesmaid';
    if (r === 'mother of the bride' || r === 'mother of the groom' || r === 'mother' || r === 'mum') return 'Mother';
    if (r === 'flower girl') return 'Flower Girl';
    if (r === 'junior bridesmaid') return 'Junior Bridesmaid';
    if (r === 'guest') return 'Guest';
    return '';
  }

  function rvBuildPartyNameMap_(members) {
    var map = {};
    var counts = {};
    (members || []).forEach(function(m) {
      var prefix = rvNormalizeRole_(m.role || m.memberRole || '');
      if (!prefix) return;
      counts[prefix] = (counts[prefix] || 0) + 1;
      var name = String(m.name || '').trim();
      if (name) map[prefix + ' ' + counts[prefix]] = name;
    });
    return map;
  }

  /* ═══════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════ */

  function render(data) {
    renderHeader(data);
    renderArtistRail(data.artists || []);
    renderTimeline(data.timeline || []);
    renderUnscheduled(data.unscheduled || []);
    renderBridalParty(data.bridalParty || []);
    renderFooter(data);
  }

  // LIVE BRIDE CONTEXT (2026-09-10, item 6 of BRIEFS/BUILD-bridal-hub-ux-fixes-
  // 2026-09-10.md). Re-derives ONLY brideName/weddingDateDisplay from the live
  // customer record; every other field (service times, artist assignments,
  // timeline) stays exactly as published, per Jess's explicit Option A scope.
  //
  // BUG FOUND + FIXED 2026-09-10 (BRIEFS/RESULTS-runsheet-view-live-bride-
  // verify-2026-09-10.md, live Cowork test): this originally shipped assuming
  // window._jvRsLiveBride is "simply absent for team/token audiences by
  // construction, same gate" -- that assumption was WRONG for a ?token=/?key=
  // visit specifically. sections/jv-runsheet-view.liquid's gate is
  // `section.settings.audience == 'bride' and customer` -- section.settings.
  // audience is a PAGE-TEMPLATE SETTING (this page is always configured
  // audience: 'bride'), not a per-request value. Appending ?token= does not
  // change it, so a logged-in customer opening ANY bride's token link still
  // gets _jvRsLiveBride emitted with THEIR OWN name/date, which then
  // unconditionally overwrote the token'd runsheet's correct bride at the
  // top of the page (confirmed live: body content, i.e. venue/times, stayed
  // correct, only the header/hero name+date were wrong). Not a data leak
  // (nothing of the token'd bride was exposed) but actively misleading about
  // whose runsheet is being viewed.
  //
  // Fix: gate on the `audience` JS variable already defined at the top of
  // this file, which DOES correctly reclassify to 'token' for a ?token=/
  // ?key= visit (URLSearchParams check, module-load time) -- the same
  // detection this file already trusts elsewhere (var audience = ...,
  // ~line 15). Reusing it here, rather than writing a second parallel
  // token-detection check, is deliberate: one source of truth for "is this a
  // token visit" so the two can never drift apart again.
  function liveBrideName_() {
    if (audience !== 'bride') return '';
    var lb = window._jvRsLiveBride;
    if (!lb || !lb.firstName) return '';
    return lb.lastName ? (lb.firstName + ' ' + lb.lastName) : lb.firstName;
  }
  function liveWeddingDateDisplay_() {
    if (audience !== 'bride') return '';
    var lb = window._jvRsLiveBride;
    return (lb && lb.weddingDateDisplay) ? lb.weddingDateDisplay : '';
  }

  function renderHeader(data) {
    var b = data.booking || {};
    var liveName = liveBrideName_();
    var liveDate = liveWeddingDateDisplay_();
    $brideName.textContent = liveName || b.brideName || '';
    $weddingDate.textContent = liveDate || b.weddingDateDisplay || '';
    if (b.readyBy) {
      $readyBy.textContent = 'Ready by ' + b.readyBy;
      $readyBy.style.display = '';
    } else {
      $readyBy.style.display = 'none';
    }

    populateHero_(b);

    var v = data.version || {};
    $versionText.textContent = formatRelativeTime(v.publishedAt);
    $versionDot.className = 'jv-rv__version-dot';
  }

  // Populates the dark hero block from a booking-like object. Called from
  // renderHeader (after a successful runsheet fetch) AND from the NO_RUNSHEET
  // empty-state branch so the bride still sees her name + wedding date even
  // before her runsheet is published. Liquid pre-populates the bride's first
  // name from {{ customer.first_name }} server-side, only overwrites when the
  // payload actually carries a value.
  function populateHero_(booking) {
    if (!booking) return;
    var $heroName    = document.getElementById('jvRvHeroName');
    var $heroDate    = document.getElementById('jvRvHeroDate');
    var $heroVenue   = document.getElementById('jvRvHeroVenue');
    // P1-1 fix (2026-08-28, Stephanie Garland audit): readyBy was computed and
    // written into the now-hidden .jv-rv__header, never into the hero the
    // page actually shows. This is the bride's answer to "what time do I
    // need to be ready by", the deadline every other block on the page
    // exists to hit, so it belongs in the hero, not buried or invisible.
    var $heroReadyBy = document.getElementById('jvRvHeroReadyBy');
    // LIVE BRIDE CONTEXT override (2026-09-10): prefer the live customer
    // record's name/date over the frozen blob's booking.brideName/
    // weddingDateDisplay when available. window._jvRsLiveBride is only ever
    // set for a genuine logged-in bride visit (see liveBrideName_ above),
    // this does NOT reintroduce the cross-customer flash the 2026-08-20 fix
    // closed, since that fix's synchronous inline script already blanks
    // $heroName's Liquid-prefilled text for a ?token=/?key= visit BEFORE this
    // function ever runs, and _jvRsLiveBride is simply absent for team/token
    // audiences in the first place (the Liquid block that sets it shares the
    // exact same `audience == 'bride' and customer` gate).
    var liveName = liveBrideName_();
    var liveDate = liveWeddingDateDisplay_();
    if ($heroName  && (liveName || booking.brideName))          $heroName.textContent  = liveName || booking.brideName;
    if ($heroDate  && (liveDate || booking.weddingDateDisplay)) $heroDate.textContent  = liveDate || booking.weddingDateDisplay;
    if ($heroVenue && booking.venue)              $heroVenue.textContent = booking.venue;
    if ($heroReadyBy) {
      $heroReadyBy.textContent = booking.readyBy ? ('Ready by ' + booking.readyBy) : '';
    }
  }

  function renderArtistRail(artists) {
    // Clear all except the "All" pill
    var existing = $artistRail.querySelectorAll('.jv-rv__artist-pill:not(#jvRvPillAll)');
    for (var i = 0; i < existing.length; i++) existing[i].remove();

    var viewerPersonId = currentData && currentData.viewer ? currentData.viewer.personId : null;

    for (var a = 0; a < artists.length; a++) {
      var artist = artists[a];
      var pill = document.createElement('button');
      pill.className = 'jv-rv__artist-pill';
      pill.dataset.artistId = artist.id;

      if (viewerPersonId && viewerPersonId === 'artist_' + artist.name) {
        pill.classList.add('jv-rv__artist-pill--self');
      }

      var label = artist.name;
      if (artist.arriveAt) label += ' · ' + artist.arriveAt;

      if (artist.avatarUrl) {
        var img = document.createElement('img');
        img.className = 'jv-rv__pill-avatar';
        img.src = artist.avatarUrl;
        img.alt = artist.name;
        pill.appendChild(img);
      }

      var span = document.createElement('span');
      span.className = 'jv-rv__pill-label';
      span.textContent = artist.name;
      pill.appendChild(span);

      if (artist.arriveAt) {
        var meta = document.createElement('span');
        meta.className = 'jv-rv__pill-meta';
        meta.textContent = artist.arriveAt;
        pill.appendChild(meta);
      }

      pill.addEventListener('click', handleArtistFilter);
      $artistRail.appendChild(pill);
    }
  }

  /**
   * Builds the sticky per-artist header row above the timeline grid
   * (Column 1 Times, Column 2 Artist #1, Column 3 Artist #2, per Jess
   * 2026-09-03). Only rendered when there's more than one artist -- a
   * single-artist bride keeps the plain flat-list layout (.jv-rv__slot
   * falls back out of grid mode when --artist-count is 1, same column
   * count as before, no header needed).
   */
  function renderColHeaders(artists) {
    if (!$colHeaders) return;
    $colHeaders.innerHTML = '';
    if (!artists || artists.length < 2) return;

    var timeSpacer = document.createElement('div');
    $colHeaders.appendChild(timeSpacer);

    for (var a = 0; a < artists.length; a++) {
      var artist = artists[a];
      var header = document.createElement('div');
      header.className = 'jv-rv__col-header';
      if (artist.avatarUrl) {
        var img = document.createElement('img');
        img.className = 'jv-rv__col-header-avatar';
        img.src = artist.avatarUrl;
        img.alt = '';
        header.appendChild(img);
      }
      var label = document.createElement('span');
      label.textContent = artist.name;
      header.appendChild(label);
      $colHeaders.appendChild(header);
    }
  }

  function renderTimeline(slots) {
    $timeline.innerHTML = '';

    var artists = (currentData && currentData.artists) ? currentData.artists : [];
    var artistCount = artists.length || 1;
    $timeline.style.setProperty('--artist-count', artistCount);
    $timeline.classList.toggle('jv-rv__timeline--multi-artist', artists.length >= 2);
    if ($colHeaders) $colHeaders.style.setProperty('--artist-count', artistCount);
    renderColHeaders(artists);

    // Build ordered artist-id array so each block can be placed in the right column
    var artistIds = [];
    for (var a = 0; a < artists.length; a++) {
      artistIds.push(artists[a].id);
    }

    var viewerPersonId = currentData && currentData.viewer ? currentData.viewer.personId : null;

    for (var s = 0; s < slots.length; s++) {
      var slot = slots[s];
      var slotEl = document.createElement('div');
      slotEl.className = 'jv-rv__slot';

      var timeEl = document.createElement('div');
      timeEl.className = 'jv-rv__slot-time';
      timeEl.textContent = formatSlotTime(slot.slotStart);
      slotEl.appendChild(timeEl);

      if (artistIds.length > 1) {
        // Multi-artist: one column cell per artist so blocks land in the right column
        var colCells = [];
        for (var c = 0; c < artistIds.length; c++) {
          var colEl = document.createElement('div');
          colEl.className = 'jv-rv__slot-col';
          colEl.dataset.colArtistId = artistIds[c];
          slotEl.appendChild(colEl);
          colCells.push(colEl);
        }
        for (var b = 0; b < slot.blocks.length; b++) {
          var block = slot.blocks[b];
          var card = createBlockCard(block, viewerPersonId);
          var blockArtistId = block.artistId ? artistIdFromName(block.artistId) : '';
          var colIdx = artistIds.indexOf(blockArtistId);
          if (colIdx !== -1) {
            colCells[colIdx].appendChild(card);
          } else {
            // Fallback: append to first column if artist not found
            colCells[0].appendChild(card);
          }
        }
      } else {
        // Single artist: flat list (original behaviour)
        var blocksEl = document.createElement('div');
        blocksEl.className = 'jv-rv__slot-blocks';
        for (var b = 0; b < slot.blocks.length; b++) {
          blocksEl.appendChild(createBlockCard(slot.blocks[b], viewerPersonId));
        }
        slotEl.appendChild(blocksEl);
      }

      $timeline.appendChild(slotEl);
    }

    // Add now-line if wedding day
    addNowLine();
  }

  function createBlockCard(block, viewerPersonId) {
    var card = document.createElement('div');
    card.className = 'jv-rv__block';
    card.dataset.blockId = block.id;
    card.dataset.artistId = block.artistId ? artistIdFromName(block.artistId) : '';
    card.tabIndex = 0;
    card.setAttribute('role', 'button');

    // Mine highlight
    var isMine = false;
    if (viewerPersonId === 'bride' && block.isBride) isMine = true;
    if (viewerPersonId && viewerPersonId.indexOf('artist_') === 0 && block.artistName && viewerPersonId === 'artist_' + block.artistName) isMine = true;
    if (isMine) card.classList.add('jv-rv__block--mine');

    // Info section
    var info = document.createElement('div');
    info.className = 'jv-rv__block-info';

    var person = document.createElement('div');
    person.className = 'jv-rv__block-person';
    person.textContent = rvNameMap_[block.personLabel] || block.personLabel;
    info.appendChild(person);

    var svcName = document.createElement('div');
    svcName.className = 'jv-rv__block-service';
    svcName.textContent = block.serviceLabel || block.service;
    info.appendChild(svcName);

    card.appendChild(info);

    // Meta section
    var metaEl = document.createElement('div');
    metaEl.className = 'jv-rv__block-meta';

    if (block.artistName) {
      var chip = document.createElement('span');
      chip.className = 'jv-rv__block-artist-chip';
      var chipAvatarUrl = artistAvatarByName_(block.artistName);
      if (chipAvatarUrl) {
        var chipAvatar = document.createElement('img');
        chipAvatar.className = 'jv-rv__chip-avatar';
        chipAvatar.src = chipAvatarUrl;
        chipAvatar.alt = '';
        chip.appendChild(chipAvatar);
      }
      var chipLabel = document.createElement('span');
      chipLabel.textContent = block.artistName;
      chip.appendChild(chipLabel);
      metaEl.appendChild(chip);
    }

    if (block.durationMin) {
      var dur = document.createElement('span');
      dur.className = 'jv-rv__block-duration';
      dur.textContent = block.durationMin + 'min';
      metaEl.appendChild(dur);
    }

    var chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    chevron.setAttribute('viewBox', '0 0 24 24');
    chevron.setAttribute('class', 'jv-rv__block-chevron');
    var polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    polyline.setAttribute('points', '9 18 15 12 9 6');
    chevron.appendChild(polyline);
    metaEl.appendChild(chevron);

    card.appendChild(metaEl);

    // Click handler
    card.addEventListener('click', function() { openBlockModal(block); });
    card.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openBlockModal(block); }
    });

    return card;
  }

  function renderUnscheduled(items) {
    if (!items.length) { $unscheduled.style.display = 'none'; return; }
    $unscheduled.style.display = '';
    $unscheduledList.innerHTML = '';

    var viewerPersonId = currentData && currentData.viewer ? currentData.viewer.personId : null;
    for (var i = 0; i < items.length; i++) {
      $unscheduledList.appendChild(createBlockCard(items[i], viewerPersonId));
    }
  }

  function renderBridalParty(party) {
    $partyList.innerHTML = '';
    if (!party.length) { $party.style.display = 'none'; return; }
    $party.style.display = '';

    for (var p = 0; p < party.length; p++) {
      var person = party[p];
      var row = document.createElement('div');
      row.className = 'jv-rv__party-person';

      var nameEl = document.createElement('span');
      nameEl.className = 'jv-rv__party-name';
      nameEl.textContent = person.label;
      row.appendChild(nameEl);

      if (person.artistId) {
        var artistEl = document.createElement('span');
        artistEl.className = 'jv-rv__party-artist';
        artistEl.textContent = person.artistId;
        row.appendChild(artistEl);
      }

      if (person.services.length) {
        var linkBtn = document.createElement('button');
        linkBtn.className = 'jv-rv__party-service-link';
        linkBtn.textContent = person.services.length + ' service' + (person.services.length > 1 ? 's' : '');
        linkBtn.dataset.blockId = person.services[0];
        linkBtn.addEventListener('click', handleScrollToBlock);
        row.appendChild(linkBtn);
      }

      $partyList.appendChild(row);
    }
  }

  function renderFooter(data) {
    var v = data.version || {};
    $publishedAt.textContent = 'Last published: ' + (v.publishedAtDisplay || '—');

    if ($pdfBtn && data.links && data.links.pdfUrl) {
      $pdfBtn.href = data.links.pdfUrl;
      $pdfBtn.style.display = '';
      if (document.documentElement.classList.contains('jv-in-app')) {
        $pdfBtn.addEventListener('click', function(e) {
          e.preventDefault();
          inAppSharePdf(data.links.pdfUrl);
        });
      }
    }

    if ($builderBtn && data.links && data.links.builderUrl && data.viewer &&
        (data.viewer.role === 'admin' || data.viewer.role === 'lead')) {
      $builderBtn.href = data.links.builderUrl;
      $builderBtn.style.display = '';
    }

    if ($modalPdfBtn && data.links && data.links.pdfUrl) {
      $modalPdfBtn.href = data.links.pdfUrl;
      $modalPdfBtn.style.display = '';
      if (document.documentElement.classList.contains('jv-in-app')) {
        $modalPdfBtn.addEventListener('click', function(e) {
          e.preventDefault();
          inAppSharePdf(data.links.pdfUrl);
        });
      }
    }
  }

  /* ═══════════════════════════════════════════════════════════
     ARTIST FILTER
     ═══════════════════════════════════════════════════════════ */

  function handleArtistFilter(e) {
    var btn = e.currentTarget;
    var artistId = btn.dataset.artistId;

    // Update active pill
    var pills = $artistRail.querySelectorAll('.jv-rv__artist-pill');
    for (var i = 0; i < pills.length; i++) pills[i].classList.remove('jv-rv__artist-pill--active');
    btn.classList.add('jv-rv__artist-pill--active');

    activeArtist = artistId;

    // Filter blocks
    var blocks = $timeline.querySelectorAll('.jv-rv__block');
    for (var b = 0; b < blocks.length; b++) {
      if (artistId === 'all') {
        blocks[b].classList.remove('jv-rv__block--hidden');
      } else {
        blocks[b].classList.toggle('jv-rv__block--hidden', blocks[b].dataset.artistId !== artistId);
      }
    }
  }

  /* ═══════════════════════════════════════════════════════════
     BLOCK MODAL
     ═══════════════════════════════════════════════════════════ */

  function openBlockModal(block) {
    $modalPerson.textContent = rvNameMap_[block.personLabel] || block.personLabel || '';
    $modalService.textContent = block.serviceLabel || block.service || '';
    $modalTime.textContent = block.startTimeDisplay
      ? block.startTimeDisplay + ' — ' + block.endTime + ' (' + block.durationMin + ' min)'
      : (block.durationMin ? block.durationMin + ' min (unscheduled)' : '');

    // Build swipeable cards
    buildModalCards(block);

    // Show modal
    $modalBackdrop.style.display = '';
    document.body.style.overflow = 'hidden';
    trapFocus($modal);
  }

  function closeModal() {
    $modalBackdrop.style.display = 'none';
    document.body.style.overflow = '';
    cardIndex = 0;
  }

  /* ── Card carousel builder ──────────────────────────────── */

  function buildModalCards(block) {
    $cardTrack.innerHTML = '';
    $cardDots.innerHTML = '';
    cardIndex = 0;

    var cards = [];

    // Brief: runsheet-viewer-service-popup-person-card-match-2026-05-19.md
    // Rich path: when the runsheet blob carries a `people` map (any audience),
    // render the per-person card set matching the runsheet builder Page 3.
    // Sparse fallback (legacy blobs without `people`): the previous 3-card set
    // sourced from currentData.profile (bride-only). Graceful degrade.
    var peopleMap = (currentData && currentData.people) || null;
    var person = (peopleMap && block && block.personId) ? peopleMap[block.personId] : null;

    if (person) {
      // Helper: wrap each card builder in try/catch so a single failing
      // builder can't leave subsequent cards as empty DOM nodes silently.
      function safePush_(label, fn) {
        try {
          var c = fn();
          if (c) cards.push(c);
        } catch (e) {
          if (window.console) console.error('[runsheet-view] card builder "' + label + '" threw:', e);
        }
      }

      // Bride: 3 cards (Overview / Inspiration / Trial)
      // Party member: 2 cards (Overview / Inspiration)
      // Vision + Blueprint cards intentionally NOT rendered here — that info
      // lives on the runsheet builder for planning; the bride/team viewer is
      // a wedding-morning quick reference.
      safePush_('Identity',    function(){ return buildPersonIdentityCard(person, block); });
      safePush_('Inspiration', function(){ return buildPersonInspirationCard(person, block) || buildInspirationCard(block); });
      if (person.kind === 'bride') {
        safePush_('Trial',     function(){ return buildPersonTrialCard(person, block); });
      }
    } else {
      // ── Sparse fallback: legacy blob, no `people` map ──
      cards.push(buildOnboardingCard(block));
      var inspoCardLegacy = buildInspirationCard(block);
      if (inspoCardLegacy) cards.push(inspoCardLegacy);
      var trialCardLegacy = buildTrialCard(block);
      if (trialCardLegacy) cards.push(trialCardLegacy);
    }

    cardCount = cards.length;

    for (var i = 0; i < cards.length; i++) {
      $cardTrack.appendChild(cards[i]);
    }

    // Build dots
    if (cards.length > 1) {
      for (var d = 0; d < cards.length; d++) {
        var dot = document.createElement('button');
        dot.className = 'jv-rv__card-dot' + (d === 0 ? ' jv-rv__card-dot--active' : '');
        dot.dataset.index = d;
        dot.setAttribute('aria-label', 'Go to card ' + (d + 1));
        dot.addEventListener('click', function() {
          goToCard(parseInt(this.dataset.index, 10));
        });
        $cardDots.appendChild(dot);
      }
    }

    // Touch swipe
    setupCardSwipe();
    goToCard(0);
  }

  function buildOnboardingCard(block) {
    var card = document.createElement('div');
    card.className = 'jv-rv__card';

    var profile = (currentData && currentData.profile) || {};
    var booking = (currentData && currentData.booking) || {};

    // Artist row
    var artistRow = document.createElement('div');
    artistRow.className = 'jv-rv__modal-artist';
    var avatarEl = document.createElement('div');
    avatarEl.className = 'jv-rv__modal-artist-avatar';
    var modalAvatarUrl = artistAvatarByName_(block.artistName);
    if (modalAvatarUrl) {
      var modalAvatarImg = document.createElement('img');
      modalAvatarImg.src = modalAvatarUrl;
      modalAvatarImg.alt = '';
      avatarEl.appendChild(modalAvatarImg);
    } else {
      avatarEl.textContent = (block.artistName || 'U').charAt(0).toUpperCase();
    }
    var artistNameEl = document.createElement('span');
    artistNameEl.textContent = block.artistName || 'Unassigned';
    artistRow.appendChild(avatarEl);
    artistRow.appendChild(artistNameEl);
    card.appendChild(artistRow);

    // Contact details
    var details = document.createElement('div');
    details.className = 'jv-rv__card-details';

    if (profile.phone) {
      details.appendChild(buildDetailRow('Phone', profile.phone, 'tel:' + profile.phone));
    }
    if (profile.email) {
      details.appendChild(buildDetailRow('Email', profile.email, 'mailto:' + profile.email));
    }
    if (booking.weddingDateDisplay) {
      details.appendChild(buildDetailRow('Wedding', booking.weddingDateDisplay));
    }
    if (booking.venue) {
      details.appendChild(buildDetailRow('Venue', booking.venue));
    }

    // Onboarding status
    var onboardingLabel = profile.onboardingComplete ? 'Complete' : 'Pending';
    var onboardingRow = buildDetailRow('Onboarding', onboardingLabel);
    var badge = onboardingRow.querySelector('.jv-rv__detail-value');
    if (badge) badge.classList.add(profile.onboardingComplete ? 'jv-rv__detail-value--ok' : 'jv-rv__detail-value--pending');
    details.appendChild(onboardingRow);

    // Notes
    if (block.notes) {
      details.appendChild(buildDetailRow('Notes', block.notes));
    }

    card.appendChild(details);

    // Folder links
    var links = document.createElement('div');
    links.className = 'jv-rv__card-links';

    if (profile.clientFolderUrl) {
      links.appendChild(buildFolderLink('Client Files', profile.clientFolderUrl));
    }
    if (profile.inspoFolderUrl) {
      links.appendChild(buildFolderLink('Inspiration Folder', profile.inspoFolderUrl));
    }
    if (profile.trialFolderUrl) {
      links.appendChild(buildFolderLink('Trial Folder', profile.trialFolderUrl));
    }

    if (links.children.length > 0) card.appendChild(links);

    // Card label
    var label = document.createElement('div');
    label.className = 'jv-rv__card-label';
    label.textContent = 'Overview';
    card.insertBefore(label, card.firstChild);

    return card;
  }

  function buildInspirationCard(block) {
    if (!currentData || !currentData.inspiration) return null;
    var inspo = currentData.inspiration;
    var personId = block.inspirationPersonId || block.personId;
    var thumbs = inspo.byPerson && inspo.byPerson[personId];
    var hasLink = inspo.folderUrl;

    if ((!thumbs || !thumbs.length) && !hasLink) return null;

    var card = document.createElement('div');
    card.className = 'jv-rv__card';

    var label = document.createElement('div');
    label.className = 'jv-rv__card-label';
    label.textContent = 'Inspiration';
    card.appendChild(label);

    if (thumbs && thumbs.length) {
      lightboxImages = thumbs;
      var gallery = document.createElement('div');
      gallery.className = 'jv-rv__inspo-gallery jv-rv__inspo-gallery--grid';

      for (var i = 0; i < thumbs.length; i++) {
        var img = document.createElement('img');
        img.className = 'jv-rv__inspo-thumb';
        img.src = thumbs[i].thumbUrl;
        img.alt = thumbs[i].name || 'Inspiration';
        img.dataset.index = i;
        img.dataset.source = 'inspo';
        img.tabIndex = 0;
        img.addEventListener('click', openLightboxFromThumb);
        img.addEventListener('keydown', function(e) {
          if (e.key === 'Enter') { e.preventDefault(); openLightboxFromThumb.call(this, e); }
        });
        gallery.appendChild(img);
      }
      card.appendChild(gallery);
    }

    if (hasLink) {
      var link = document.createElement('a');
      link.className = 'jv-rv__card-folder-link';
      link.href = inspo.folderUrl;
      link.target = '_blank';
      link.textContent = 'Open folder in Drive';
      card.appendChild(link);
    }

    return card;
  }

  function buildTrialCard(block) {
    var profile = (currentData && currentData.profile) || {};
    var trialPhotos = (currentData && currentData.trialPhotos) || {};
    var hasTrial = profile.trialRequired || profile.trialDate || (trialPhotos.thumbs && trialPhotos.thumbs.length);

    if (!hasTrial) return null;

    var card = document.createElement('div');
    card.className = 'jv-rv__card';

    var label = document.createElement('div');
    label.className = 'jv-rv__card-label';
    label.textContent = 'Trial';
    card.appendChild(label);

    // Trial date
    if (profile.trialDate) {
      var dateEl = document.createElement('div');
      dateEl.className = 'jv-rv__card-trial-date';
      dateEl.textContent = 'Trial date: ' + profile.trialDate;
      card.appendChild(dateEl);
    }

    // Trial photos
    var thumbs = trialPhotos.thumbs || [];
    if (thumbs.length) {
      var gallery = document.createElement('div');
      gallery.className = 'jv-rv__inspo-gallery jv-rv__inspo-gallery--grid';

      for (var i = 0; i < thumbs.length; i++) {
        var img = document.createElement('img');
        img.className = 'jv-rv__inspo-thumb';
        img.src = thumbs[i].thumbUrl;
        img.alt = thumbs[i].name || 'Trial photo';
        img.dataset.index = i;
        img.dataset.source = 'trial';
        img.tabIndex = 0;
        img.addEventListener('click', function(e) {
          lightboxImages = trialPhotos.thumbs;
          openLightboxFromThumb(e);
        });
        gallery.appendChild(img);
      }
      card.appendChild(gallery);
    } else {
      var emptyMsg = document.createElement('p');
      emptyMsg.className = 'jv-rv__card-empty-msg';
      emptyMsg.textContent = 'No trial photos uploaded yet.';
      card.appendChild(emptyMsg);
    }

    // Folder link
    if (trialPhotos.folderUrl) {
      var link = document.createElement('a');
      link.className = 'jv-rv__card-folder-link';
      link.href = trialPhotos.folderUrl;
      link.target = '_blank';
      link.textContent = 'Open trial folder in Drive';
      card.appendChild(link);
    }

    return card;
  }

  /* ═══════════════════════════════════════════════════════════
     PER-PERSON CARDS (brief: runsheet-viewer-service-popup-person-card-match-2026-05-19)
     Read-only. No edit affordance. Driven by currentData.people[block.personId].
     ═══════════════════════════════════════════════════════════ */

  function buildPersonIdentityCard(person, block) {
    var card = document.createElement('div');
    card.className = 'jv-rv__card';

    // Card label
    var label = document.createElement('div');
    label.className = 'jv-rv__card-label';
    label.textContent = 'Overview';
    card.appendChild(label);

    // ── Allergies banner (top, red, only when present) ──
    if (person.allergies && String(person.allergies).trim()) {
      var banner = document.createElement('div');
      banner.className = 'jv-rv__allergies-banner';
      var bIcon = document.createElement('span');
      bIcon.className = 'jv-rv__allergies-icon';
      bIcon.setAttribute('aria-hidden', 'true');
      bIcon.textContent = '!';
      var bBody = document.createElement('div');
      var bStrong = document.createElement('strong');
      bStrong.textContent = 'Allergies: ';
      bBody.appendChild(bStrong);
      bBody.appendChild(document.createTextNode(String(person.allergies)));
      banner.appendChild(bIcon);
      banner.appendChild(bBody);
      card.appendChild(banner);
    }

    // ── Person identity row (avatar + name + role) ──
    var idRow = document.createElement('div');
    idRow.className = 'jv-rv__modal-artist';
    var avatar = document.createElement('div');
    avatar.className = 'jv-rv__modal-artist-avatar';
    if (person.profilePicUrl) {
      var img = document.createElement('img');
      img.src = person.profilePicUrl;
      img.alt = '';
      avatar.appendChild(img);
    } else {
      avatar.textContent = (person.name || 'U').charAt(0).toUpperCase();
    }
    var nameWrap = document.createElement('span');
    var nameStrong = document.createElement('strong');
    nameStrong.textContent = person.name || 'Person';
    nameWrap.appendChild(nameStrong);
    if (person.role) {
      nameWrap.appendChild(document.createTextNode(' · ' + person.role));
    }
    // Trial-only tag (member, low-urgency rose)
    if (person.kind === 'member' && person.trialOnly) {
      var tag = document.createElement('span');
      tag.className = 'jv-rv__trial-only-tag';
      tag.textContent = 'Trial only';
      nameWrap.appendChild(tag);
    }
    idRow.appendChild(avatar);
    idRow.appendChild(nameWrap);
    card.appendChild(idRow);

    // ── Service-of-this-block (artist row) ──
    var serviceRow = document.createElement('div');
    serviceRow.className = 'jv-rv__detail-row';
    var srvLbl = document.createElement('span');
    srvLbl.className = 'jv-rv__detail-label';
    srvLbl.textContent = 'Artist';
    var srvVal = document.createElement('span');
    srvVal.className = 'jv-rv__detail-value';
    srvVal.textContent = (block && block.artistName) || 'Unassigned';
    serviceRow.appendChild(srvLbl);
    serviceRow.appendChild(srvVal);

    var details = document.createElement('div');
    details.className = 'jv-rv__card-details';
    details.appendChild(serviceRow);

    // ── Contact ──
    if (person.phone) details.appendChild(buildDetailRow('Phone', person.phone, 'tel:' + person.phone));
    if (person.email) details.appendChild(buildDetailRow('Email', person.email, 'mailto:' + person.email));

    // ── Onboarding badge (bride only) ──
    if (person.kind === 'bride') {
      var oRow = buildDetailRow('Onboarding', person.onboardingComplete ? 'Complete' : 'Pending');
      var oBadge = oRow.querySelector('.jv-rv__detail-value');
      if (oBadge) oBadge.classList.add(person.onboardingComplete ? 'jv-rv__detail-value--ok' : 'jv-rv__detail-value--pending');
      details.appendChild(oRow);
    }

    // Block notes (per-service)
    if (block && block.notes) details.appendChild(buildDetailRow('Notes', block.notes));

    card.appendChild(details);

    // ── Folder links (bride only — members don't have client/trial folders distinct from bride's) ──
    if (person.kind === 'bride') {
      var links = document.createElement('div');
      links.className = 'jv-rv__card-links';
      if (person.clientFolderUrl) links.appendChild(buildFolderLink('Client Files', person.clientFolderUrl));
      if (person.inspoFolderUrl)  links.appendChild(buildFolderLink('Inspiration Folder', person.inspoFolderUrl));
      if (person.trialFolderUrl)  links.appendChild(buildFolderLink('Trial Folder', person.trialFolderUrl));
      if (links.children.length) card.appendChild(links);
    } else if (person.inspoFolderUrl) {
      // Member only has their own inspiration folder
      var memberLinks = document.createElement('div');
      memberLinks.className = 'jv-rv__card-links';
      memberLinks.appendChild(buildFolderLink('Inspiration Folder', person.inspoFolderUrl));
      card.appendChild(memberLinks);
    }

    return card;
  }

  function buildPersonVisionCard(person) {
    var card = document.createElement('div');
    card.className = 'jv-rv__card';

    var label = document.createElement('div');
    label.className = 'jv-rv__card-label';
    label.textContent = person.kind === 'bride' ? 'Wedding Vision' : 'Preferences';
    card.appendChild(label);

    if (person.kind === 'bride') {
      // Vision word chips
      if (person.visionWords && person.visionWords.length) {
        var chipWrap = document.createElement('div');
        chipWrap.className = 'jv-rv__vision-chips';
        person.visionWords.forEach(function(w) {
          var chip = document.createElement('span');
          chip.className = 'jv-rv__vision-chip';
          chip.textContent = w;
          chipWrap.appendChild(chip);
        });
        card.appendChild(chipWrap);
      }

      var details = document.createElement('div');
      details.className = 'jv-rv__card-details';
      if (person.partnerName)   details.appendChild(buildDetailRow('Partner', person.partnerName));
      if (person.hairstylePref) details.appendChild(buildDetailRow('Hairstyle', person.hairstylePref));
      if (person.makeupVibe)    details.appendChild(buildDetailRow('Makeup vibe', person.makeupVibe));
      if (person.beautyNotes)   details.appendChild(buildDetailRow('Beauty notes', person.beautyNotes));
      card.appendChild(details);
    } else {
      // Member
      var mDetails = document.createElement('div');
      mDetails.className = 'jv-rv__card-details';
      if (person.hairStyle)     mDetails.appendChild(buildDetailRow('Hairstyle', person.hairStyle));
      if (person.colourPalette) mDetails.appendChild(buildDetailRow('Colour palette', person.colourPalette));
      if (person.skinConcerns)  mDetails.appendChild(buildDetailRow('Skin concerns', person.skinConcerns));
      card.appendChild(mDetails);

      if (!person.hairStyle && !person.colourPalette && !person.skinConcerns) {
        var empty = document.createElement('p');
        empty.className = 'jv-rv__card-empty-msg';
        empty.textContent = person._stub
          ? 'No profile information yet — this guest has not been onboarded.'
          : 'No preferences recorded yet.';
        card.appendChild(empty);
      }
    }

    return card;
  }

  function buildPersonBlueprintCard(person) {
    var card = document.createElement('div');
    card.className = 'jv-rv__card';

    var label = document.createElement('div');
    label.className = 'jv-rv__card-label';
    label.textContent = 'Beauty Blueprint';
    card.appendChild(label);

    var details = document.createElement('div');
    details.className = 'jv-rv__card-details';
    var any = false;

    if (person.kind === 'bride') {
      var hs = person.hairSituation || {};
      var ms = person.makeupSituation || {};
      if (hs.routine)  { details.appendChild(buildDetailRow('Hair routine', hs.routine)); any = true; }
      if (hs.products) { details.appendChild(buildDetailRow('Hair products', hs.products)); any = true; }
      if (hs.heatUse)  { details.appendChild(buildDetailRow('Heat use', hs.heatUse)); any = true; }
      if (hs.concerns) { details.appendChild(buildDetailRow('Hair concerns', hs.concerns)); any = true; }
      if (hs.veil)     { details.appendChild(buildDetailRow('Veil', hs.veil)); any = true; }
      if (ms.skinType) { details.appendChild(buildDetailRow('Skin type', ms.skinType)); any = true; }
      if (ms.actives)  { details.appendChild(buildDetailRow('Skin actives', ms.actives)); any = true; }
      if (ms.routine)  { details.appendChild(buildDetailRow('Makeup routine', ms.routine)); any = true; }
      if (ms.favs)     { details.appendChild(buildDetailRow('Favourites', ms.favs)); any = true; }
    } else {
      if (person.hairNotes)  { details.appendChild(buildDetailRow('Hair notes', person.hairNotes)); any = true; }
      if (person.inspoNotes) { details.appendChild(buildDetailRow('Inspo notes', person.inspoNotes)); any = true; }
    }

    if (!any) return null;
    card.appendChild(details);
    return card;
  }

  function buildPersonInspirationCard(person, block) {
    // Renders inspo thumbs directly from person.inspoHair / person.inspoMakeup
    // (always populated at publish time). Bride entries store Drive file IDs;
    // member entries store either IDs or full lh3 URLs. Both are normalised here.
    // Falls back to null so the caller can use buildInspirationCard (deferred byPerson path).
    var hair = (person && person.inspoHair) || [];
    var makeup = (person && person.inspoMakeup) || [];
    var folderUrl = (person && person.inspoFolderUrl) || '';
    if (!hair.length && !makeup.length && !folderUrl) return null;

    function toUrl(idOrUrl) {
      var s = String(idOrUrl || '').trim();
      if (!s) return '';
      if (s.indexOf('http://') === 0 || s.indexOf('https://') === 0) return s;
      return 'https://lh3.googleusercontent.com/d/' + s;
    }

    var card = document.createElement('div');
    card.className = 'jv-rv__card';
    var label = document.createElement('div');
    label.className = 'jv-rv__card-label';
    label.textContent = 'Inspiration';
    card.appendChild(label);

    // Build a combined thumbs array for the lightbox (hair first, then makeup)
    var combined = [];
    hair.forEach(function(x) { var u = toUrl(x); if (u) combined.push({ thumbUrl: u, name: 'Hair inspiration' }); });
    makeup.forEach(function(x) { var u = toUrl(x); if (u) combined.push({ thumbUrl: u, name: 'Makeup inspiration' }); });

    if (combined.length) {
      lightboxImages = combined;
      var gallery = document.createElement('div');
      gallery.className = 'jv-rv__inspo-gallery jv-rv__inspo-gallery--grid';
      for (var i = 0; i < combined.length; i++) {
        var img = document.createElement('img');
        img.className = 'jv-rv__inspo-thumb';
        img.src = combined[i].thumbUrl;
        img.alt = combined[i].name;
        img.dataset.index = i;
        img.dataset.source = 'inspo';
        img.loading = 'lazy';
        img.tabIndex = 0;
        img.addEventListener('click', openLightboxFromThumb);
        img.addEventListener('keydown', function(e) {
          if (e.key === 'Enter') { e.preventDefault(); openLightboxFromThumb.call(this, e); }
        });
        gallery.appendChild(img);
      }
      card.appendChild(gallery);
    }

    if (folderUrl) {
      var link = document.createElement('a');
      link.className = 'jv-rv__card-folder-link';
      link.href = folderUrl;
      link.target = '_blank';
      link.textContent = 'Open folder in Drive';
      card.appendChild(link);
    }

    return card;
  }

  function buildPersonTrialCard(person, block) {
    // Bride: only when trialRequired (independent of whether trial photos exist).
    // Member: only when trialOnly is flagged.
    if (person.kind === 'bride' && !person.trialRequired && !person.trialDate) return null;
    if (person.kind === 'member' && !person.trialOnly) return null;

    var card = document.createElement('div');
    card.className = 'jv-rv__card';
    var label = document.createElement('div');
    label.className = 'jv-rv__card-label';
    label.textContent = 'Trial';
    card.appendChild(label);

    if (person.kind === 'bride') {
      var details = document.createElement('div');
      details.className = 'jv-rv__card-details';
      if (person.trialDate) {
        var dateStr = String(person.trialDate || '');
        // Trim the timezone tail if present (e.g. "Sun Aug 30 2026 00:00:00 GMT+1000 (...)" → "Sun Aug 30 2026")
        var match = dateStr.match(/^(\w{3} \w{3} \d{1,2} \d{4})/);
        if (match) dateStr = match[1];
        details.appendChild(buildDetailRow('Trial date', dateStr));
      }
      if (person.trialRequired) details.appendChild(buildDetailRow('Status', 'Required'));
      card.appendChild(details);

      if (person.trialFolderUrl) {
        var links = document.createElement('div');
        links.className = 'jv-rv__card-links';
        links.appendChild(buildFolderLink('Trial Folder', person.trialFolderUrl));
        card.appendChild(links);
      }
    } else {
      // Member trial-only: simple advisory card
      var msg = document.createElement('p');
      msg.className = 'jv-rv__card-empty-msg';
      msg.textContent = 'This guest is part of the trial only.';
      card.appendChild(msg);
    }

    return card;
  }

  /* ── Card helper builders ──────────────────────────────── */

  function buildDetailRow(labelText, value, href) {
    var row = document.createElement('div');
    row.className = 'jv-rv__detail-row';

    var lbl = document.createElement('span');
    lbl.className = 'jv-rv__detail-label';
    lbl.textContent = labelText;
    row.appendChild(lbl);

    if (href) {
      var a = document.createElement('a');
      a.className = 'jv-rv__detail-value jv-rv__detail-value--link';
      a.href = href;
      a.textContent = value;
      if (href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) {
        // keep default behavior
      } else {
        a.target = '_blank';
      }
      row.appendChild(a);
    } else {
      var span = document.createElement('span');
      span.className = 'jv-rv__detail-value';
      span.textContent = value;
      row.appendChild(span);
    }

    return row;
  }

  function buildFolderLink(text, url) {
    var a = document.createElement('a');
    a.className = 'jv-rv__card-folder-link';
    a.href = url;
    a.target = '_blank';
    a.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" fill="none" stroke="currentColor" stroke-width="2"/></svg> ' + text;
    return a;
  }

  /* ── Card navigation / swipe ───────────────────────────── */

  function goToCard(idx) {
    if (idx < 0 || idx >= cardCount) return;
    cardIndex = idx;
    $cardTrack.style.transform = 'translateX(-' + (idx * 100) + '%)';

    var dots = $cardDots.querySelectorAll('.jv-rv__card-dot');
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('jv-rv__card-dot--active', i === idx);
    }
  }

  function setupCardSwipe() {
    var startX = 0;
    var startY = 0;
    var tracking = false;

    $cardTrack.addEventListener('touchstart', function(e) {
      if (e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      tracking = true;
    }, { passive: true });

    $cardTrack.addEventListener('touchend', function(e) {
      if (!tracking) return;
      tracking = false;
      var endX = e.changedTouches[0].clientX;
      var endY = e.changedTouches[0].clientY;
      var dx = endX - startX;
      var dy = endY - startY;

      // Only swipe if horizontal movement > vertical and > 50px
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
        if (dx < 0) goToCard(cardIndex + 1);
        else goToCard(cardIndex - 1);
      }
    }, { passive: true });
  }

  /* ═══════════════════════════════════════════════════════════
     LIGHTBOX
     ═══════════════════════════════════════════════════════════ */

  function openLightboxFromThumb(e) {
    lightboxIndex = parseInt(e.currentTarget.dataset.index, 10) || 0;
    showLightbox();
  }

  function showLightbox() {
    if (!lightboxImages.length) return;
    var item = lightboxImages[lightboxIndex];
    // Use full-size URL
    $lightboxImg.src = item.thumbUrl.replace('s400', 's1200');
    $lightbox.style.display = '';
  }

  function closeLightbox() {
    $lightbox.style.display = 'none';
    $lightboxImg.src = '';
  }

  function lightboxNav(dir) {
    lightboxIndex = (lightboxIndex + dir + lightboxImages.length) % lightboxImages.length;
    showLightbox();
  }

  /* ═══════════════════════════════════════════════════════════
     POLLING
     ═══════════════════════════════════════════════════════════ */

  function startPolling() {
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = setInterval(poll, pollInterval * 1000);

    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'hidden') {
        if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
      } else {
        poll(); // poll once on return
        pollTimer = setInterval(poll, pollInterval * 1000);
      }
    });
  }

  function poll() {
    if (!currentEtag) return;

    var route = audience === 'token' ? 'bridge_runsheet_tokenViewPoll'
              : audience === 'team' ? 'bridge_team_pollRunsheetVersion'
              : 'bridge_bride_pollRunsheetVersion';
    bridgeCall(route).then(function(resp) {
      if (!resp || !resp.ok) return;
      if (resp.etag && resp.etag !== currentEtag) {
        handleVersionChange(resp);
      }
    }).catch(function() { /* silent poll failure */ });
  }

  function handleVersionChange(pollResp) {
    // Fetch diff
    var diffRoute = audience === 'token' ? 'bridge_runsheet_tokenViewDiff'
                 : audience === 'team' ? 'bridge_team_getRunsheetDiff'
                 : 'bridge_bride_getRunsheetDiff';
    bridgeCall(diffRoute, { clientEtag: currentEtag }).then(function(diffResp) {
      var summary = 'Runsheet updated';
      if (diffResp && diffResp.ok && diffResp.diffAvailable && diffResp.diff) {
        summary = 'Runsheet updated — ' + diffResp.diff.summary;
        diffBlockIds = [].concat(
          diffResp.diff.added || [],
          diffResp.diff.movedTime || [],
          diffResp.diff.changedArtist || [],
          diffResp.diff.changedService || []
        );
      }

      // Show toast
      showToast(summary);

      // Pulse the version dot
      $versionDot.classList.add('jv-rv__version-dot--pulse');

      // Refetch full
      var fullRoute = audience === 'token' ? 'bridge_runsheet_tokenView'
                   : audience === 'team' ? 'bridge_team_getRunsheetV2'
                   : 'bridge_bride_getRunsheet';
      bridgeCall(fullRoute).then(function(resp) {
        if (resp && resp.ok) {
          currentData = resp;
          currentEtag = resp.version && resp.version.etag;
          render(resp);
        }
      }).catch(function() {});

    }).catch(function() {
      showToast('Runsheet updated');
      // Still refetch
      fetchFull();
    });
  }

  /* ═══════════════════════════════════════════════════════════
     TOAST
     ═══════════════════════════════════════════════════════════ */

  function showToast(text) {
    $toastText.textContent = text;
    $toast.style.display = '';
    // Trigger animation
    requestAnimationFrame(function() {
      $toast.classList.add('jv-rv__toast--visible');
    });

    // Auto-hide after 10s
    setTimeout(function() { hideToast(); }, 10000);
  }

  function hideToast() {
    $toast.classList.remove('jv-rv__toast--visible');
    setTimeout(function() { $toast.style.display = 'none'; }, 400);
  }

  function highlightDiffBlocks() {
    if (!diffBlockIds.length) return;

    for (var i = 0; i < diffBlockIds.length; i++) {
      var el = $timeline.querySelector('[data-block-id="' + diffBlockIds[i] + '"]');
      if (el) el.classList.add('jv-rv__block--highlight');
    }

    // Remove highlight after 10s
    setTimeout(function() {
      var highlighted = $timeline.querySelectorAll('.jv-rv__block--highlight');
      for (var h = 0; h < highlighted.length; h++) {
        highlighted[h].classList.remove('jv-rv__block--highlight');
      }
      diffBlockIds = [];
    }, 10000);
  }

  /* ═══════════════════════════════════════════════════════════
     NOW LINE
     ═══════════════════════════════════════════════════════════ */

  function addNowLine() {
    // Only on wedding day
    if (!currentData || !currentData.booking || !currentData.booking.weddingDateISO) return;

    var today = new Date();
    var tz = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
    if (tz !== currentData.booking.weddingDateISO) return;

    var nowMin = today.getHours() * 60 + today.getMinutes();
    var slots = $timeline.querySelectorAll('.jv-rv__slot');
    if (!slots.length) return;

    // Find the right position
    for (var i = 0; i < slots.length; i++) {
      var timeText = slots[i].querySelector('.jv-rv__slot-time');
      if (!timeText) continue;
      var slotMin = parseSlotTime(timeText.textContent);
      if (slotMin > nowMin) {
        var line = document.createElement('div');
        line.className = 'jv-rv__now-line';
        slots[i].parentNode.insertBefore(line, slots[i]);
        break;
      }
    }
  }

  /* ═══════════════════════════════════════════════════════════
     SCROLL TO BLOCK
     ═══════════════════════════════════════════════════════════ */

  function handleScrollToBlock(e) {
    var blockId = e.currentTarget.dataset.blockId;
    if (!blockId) return;
    var el = document.querySelector('[data-block-id="' + blockId + '"]');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('jv-rv__block--highlight');
      setTimeout(function() { el.classList.remove('jv-rv__block--highlight'); }, 3000);
    }
  }

  /* ═══════════════════════════════════════════════════════════
     VISIBILITY HELPERS
     ═══════════════════════════════════════════════════════════ */

  // "This can take a moment." appears after 8s on the skeleton (2026-10-06).
  var _skeletonSlowTimer = null;
  function clearSkeletonSlow_() {
    if (_skeletonSlowTimer) { clearTimeout(_skeletonSlowTimer); _skeletonSlowTimer = null; }
    var el = document.getElementById('jvRvSkeletonSlow');
    if (el) el.style.display = 'none';
  }

  function showSkeleton() {
    clearSkeletonSlow_();
    _skeletonSlowTimer = setTimeout(function() {
      var el = document.getElementById('jvRvSkeletonSlow');
      if (el) el.style.display = '';
    }, 8000);
    if ($search) $search.style.display = 'none';
    $skeleton.style.display = '';
    $empty.style.display = 'none';
    $error.style.display = 'none';
    $content.style.display = 'none';
  }

  function showEmpty() {
    clearSkeletonSlow_();
    if ($search) $search.style.display = 'none';
    $skeleton.style.display = 'none';
    $empty.style.display = '';
    $error.style.display = 'none';
    $content.style.display = 'none';
  }

  var ERROR_TEXT_DEFAULT_ = '';
  function setErrorText_(text) {
    var el = document.getElementById('jvRvErrorText');
    if (!el) return;
    if (!ERROR_TEXT_DEFAULT_) ERROR_TEXT_DEFAULT_ = el.textContent;
    el.textContent = text || ERROR_TEXT_DEFAULT_;
  }

  function showError(msg, preparingText) {
    clearSkeletonSlow_();
    setErrorText_(preparingText || '');
    if ($search) $search.style.display = 'none';
    $skeleton.style.display = 'none';
    $empty.style.display = 'none';
    $error.style.display = '';
    $content.style.display = 'none';
    if ($errorDetail && audience === 'team') $errorDetail.textContent = msg || '';
  }

  function showContent() {
    clearSkeletonSlow_();
    if ($search) $search.style.display = 'none';
    $skeleton.style.display = 'none';
    $empty.style.display = 'none';
    $error.style.display = 'none';
    $content.style.display = '';
  }

  /* ═══════════════════════════════════════════════════════════
     UTILITY FUNCTIONS
     ═══════════════════════════════════════════════════════════ */

  function artistIdFromName(name) {
    return 'a_' + String(name).toLowerCase().replace(/[^a-z0-9]/g, '_');
  }

  function formatSlotTime(time24) {
    if (!time24) return '';
    var parts = time24.split(':');
    var h = parseInt(parts[0], 10);
    var m = parts[1] || '00';
    var ampm = h >= 12 ? 'pm' : 'am';
    var h12 = h % 12 || 12;
    return h12 + ':' + m + ampm;
  }

  function parseSlotTime(text) {
    var clean = text.trim().toLowerCase();
    var match = clean.match(/^(\d{1,2}):(\d{2})(am|pm)$/);
    if (!match) return 0;
    var h = parseInt(match[1], 10);
    var m = parseInt(match[2], 10);
    if (match[3] === 'pm' && h !== 12) h += 12;
    if (match[3] === 'am' && h === 12) h = 0;
    return h * 60 + m;
  }

  function formatRelativeTime(isoStr) {
    if (!isoStr) return '';
    try {
      var d = new Date(isoStr);
      if (isNaN(d.getTime())) return '';
      var diff = Math.floor((Date.now() - d.getTime()) / 1000);
      if (diff < 60) return 'Just now';
      if (diff < 3600) return Math.floor(diff / 60) + ' min ago';
      if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
      return Math.floor(diff / 86400) + 'd ago';
    } catch (e) { return ''; }
  }

  function trapFocus(container) {
    var focusable = container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    focusable[0].focus();
  }

  /* ═══════════════════════════════════════════════════════════
     BRIDE SEARCH (team audience)
     ═══════════════════════════════════════════════════════════ */

  // ── Default bride list for team runsheet viewer ─────────────────────────────

  var _rvBrideList_ = null;

  function loadRunsheetBrideList_() {
    // Use cache if already loaded this session
    if (_rvBrideList_) {
      renderRunsheetBrideList_(_rvBrideList_);
      return;
    }
    // ADMIN-ONLY BY DESIGN, AND DELIBERATELY NOT SWITCHED TO callTeam
    // (reviewed 2026-09-20 alongside the callAdmin->callTeam fix above).
    //
    // bridge_runsheet_getBrides returns the FULL bride list (every name +
    // wedding date) and is admin-gated on purpose --
    // shopifyBridge_runsheetGetBrides (ShopifyBridge.gs:4211) added that gate
    // as a 2026-06-17 SECURITY fix, because the whole list had been readable
    // by anyone who could POST the route. Repointing this at callTeam to
    // "finish" the artist fix above would silently reverse that and hand every
    // artist the entire client book. The empty-search symptom and the
    // runsheet-never-loads symptom look like one bug; only the second was one.
    //
    // Artists are NOT left without a way in: performSearch() below already
    // calls the properly team-scoped bridge_team_searchBrides (server-side
    // search, roster-authenticated through validateTeam_). It was broken for
    // them only because it routes through bridgeCall, i.e. the callAdmin bug
    // fixed above -- so with that fix, typing a bride's name works for artists
    // today. What they lose is only the browsable full list, which they were
    // never entitled to.
    //
    // A browsable artist-scoped list would need its own route returning just
    // that artist's ASSIGNED brides. That is a new capability, not a gate
    // swap, and must not be improvised here.
    // T-02 (2026-09-30, BRIEFS/HANDOFF/RESULTS-team-app-xray-fixes-2026-09-30.md):
    // a non-admin (artist) viewer has no entitlement to the full bride-book route
    // above, but IS entitled to her OWN assigned upcoming weddings. That
    // artist-scoped route already exists and is already live --
    // window.jvTeamApi.getJobs() (assets/jv-team-cloudflare.js, the D1-authorised
    // /team/jobs App Proxy route already used on the team dashboard) -- it was
    // simply never loaded on this page. Falls straight back to the existing
    // empty-list behaviour on any miss/error/flag-off, so a bride/token viewer
    // (where window.jvTeamApi is never loaded, see sections/jv-runsheet-view.liquid)
    // or an artist with the flag off sees exactly what she did before this fix.
    var _rvIsAdmin = !!(window.jvAuth && window.jvAuth.role === 'admin');
    if (!_rvIsAdmin) {
      if (window.jvTeamApi && typeof window.jvTeamApi.jobsReadEnabled === 'function' &&
          window.jvTeamApi.jobsReadEnabled()) {
        if ($searchLoading) $searchLoading.style.display = '';
        window.jvTeamApi.getJobs().then(function (data) {
          if ($searchLoading) $searchLoading.style.display = 'none';
          var jobs = (data && Array.isArray(data.jobs)) ? data.jobs : [];
          var brides = jobs.map(function (j) {
            return { name: j.clientDisplayName || '', isoDate: j.weddingDate || '', dateStr: j.weddingDate || '', jobId: j.id ? String(j.id) : '' };
          }).filter(function (b) { return b.name; });
          _rvBrideList_ = brides;
          renderRunsheetBrideList_(brides);
        }).catch(function () {
          if ($searchLoading) $searchLoading.style.display = 'none';
          renderRunsheetBrideList_([]);
        });
        return;
      }
      if ($searchLoading) $searchLoading.style.display = 'none';
      renderRunsheetBrideList_([]);
      return;
    }

    if ($searchLoading) $searchLoading.style.display = '';

    function doLoad() {
      window.jvBridge.callAdmin('bridge_runsheet_getBrides', {}).then(function(resp) {
        if ($searchLoading) $searchLoading.style.display = 'none';
        if (!resp || !resp.ok || !resp.data) return;
        var brides = resp.data || [];
        // Sort: soonest upcoming first, then most recent past
        var today = new Date(); today.setHours(0, 0, 0, 0);
        var future = [], past = [];
        brides.forEach(function(b) {
          var d = b.isoDate ? new Date(b.isoDate) : null;
          if (d && d >= today) future.push(b); else past.push(b);
        });
        past.reverse(); // most recent past at top of past section
        _rvBrideList_ = future.concat(past);
        renderRunsheetBrideList_(_rvBrideList_);
      }).catch(function() {
        if ($searchLoading) $searchLoading.style.display = 'none';
      });
    }

    if (window.jvBridge) { doLoad(); } else { waitForBridge(doLoad); }
  }

  function renderRunsheetBrideList_(brides) {
    if (!$searchResults) return;
    var term = ($searchInput ? $searchInput.value.trim() : '').toLowerCase();
    var filtered = term.length >= 1
      ? brides.filter(function(b) {
          return (b.name || '').toLowerCase().indexOf(term) !== -1 ||
                 (b.dateStr || '').toLowerCase().indexOf(term) !== -1 ||
                 (b.isoDate || '').indexOf(term) !== -1;
        })
      : brides;

    if (!filtered.length) {
      $searchResults.innerHTML = '';
      if ($searchEmpty) {
        $searchEmpty.style.display = '';
        $searchEmpty.querySelector('p').textContent = term
          ? 'No brides match "' + term + '".'
          : 'No upcoming brides found.';
      }
      return;
    }
    if ($searchEmpty) $searchEmpty.style.display = 'none';
    renderSearchResults(filtered.map(function(b) {
      return { brideName: b.name, weddingDateIso: b.isoDate, jobId: b.jobId || '' };
    }));
  }

  // ─────────────────────────────────────────────────────────────────────────────

  function showSearch() {
    if (!$search) return;
    $skeleton.style.display = 'none';
    $empty.style.display = 'none';
    $error.style.display = 'none';
    $content.style.display = 'none';
    $search.style.display = '';

    // Load the full bride list immediately so the page shows content, not a blank search box
    loadRunsheetBrideList_();
  }

  function hideSearch() {
    if ($search) $search.style.display = 'none';
  }

  function handleSearchInput() {
    if (!$searchInput) return;
    if (searchDebounce) clearTimeout(searchDebounce);

    // If the full list is loaded, filter it in-memory — no GAS call needed
    if (_rvBrideList_) {
      renderRunsheetBrideList_(_rvBrideList_);
      return;
    }

    // Fallback: list not yet loaded — hit GAS search
    var term = $searchInput.value.trim();
    if (!term || term.length < 2) {
      if ($searchResults) $searchResults.innerHTML = '';
      if ($searchEmpty) $searchEmpty.style.display = 'none';
      if ($searchLoading) $searchLoading.style.display = 'none';
      return;
    }
    var $loadingText = document.getElementById('jvRvSearchLoadingText');
    if ($loadingText) $loadingText.textContent = 'Searching...';
    if ($searchLoading) $searchLoading.style.display = '';
    if ($searchEmpty) $searchEmpty.style.display = 'none';
    searchDebounce = setTimeout(function() { performSearch(term); }, 350);
  }

  function performSearch(term) {
    if (!window.jvBridge) {
      waitForBridge(function() { performSearch(term); });
      return;
    }

    bridgeCall('bridge_team_searchBrides', { search: term, limit: 20 }).then(function(resp) {
      if ($searchLoading) $searchLoading.style.display = 'none';

      if (!resp || !resp.ok) {
        if ($searchResults) $searchResults.innerHTML = '';
        if ($searchEmpty) {
          $searchEmpty.style.display = '';
          $searchEmpty.querySelector('p').textContent = resp && resp.error ? resp.error : 'Search failed. Please try again.';
        }
        return;
      }

      var data = resp.data || resp;
      var results = data.results || [];

      if (!results.length) {
        if ($searchResults) $searchResults.innerHTML = '';
        if ($searchEmpty) {
          $searchEmpty.style.display = '';
          $searchEmpty.querySelector('p').textContent = 'No brides found. Try a different name or date.';
        }
        return;
      }

      if ($searchEmpty) $searchEmpty.style.display = 'none';
      renderSearchResults(results);
    }).catch(function(err) {
      if ($searchLoading) $searchLoading.style.display = 'none';
      if ($searchResults) $searchResults.innerHTML = '';
      if ($searchEmpty) {
        $searchEmpty.style.display = '';
        $searchEmpty.querySelector('p').textContent = 'Search error: ' + (err.message || 'Network error');
      }
    });
  }

  function renderSearchResults(results) {
    if (!$searchResults) return;
    $searchResults.innerHTML = '';

    for (var i = 0; i < results.length; i++) {
      var r = results[i];
      var card = document.createElement('button');
      card.className = 'jv-rv__search-result';
      card.dataset.brideName = r.brideName || '';
      card.dataset.weddingDate = r.weddingDateIso || '';
      card.dataset.jobId = r.jobId || '';

      var nameEl = document.createElement('span');
      nameEl.className = 'jv-rv__search-result-name';
      nameEl.textContent = r.brideName || 'Unknown';
      card.appendChild(nameEl);

      if (r.weddingDateIso) {
        var dateEl = document.createElement('span');
        dateEl.className = 'jv-rv__search-result-date';
        dateEl.textContent = formatSearchDate(r.weddingDateIso);
        card.appendChild(dateEl);
      }

      var chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      chevron.setAttribute('viewBox', '0 0 24 24');
      chevron.setAttribute('class', 'jv-rv__search-result-chevron');
      var polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
      polyline.setAttribute('points', '9 18 15 12 9 6');
      chevron.appendChild(polyline);
      card.appendChild(chevron);

      card.addEventListener('click', handleSelectBride);
      $searchResults.appendChild(card);
    }
  }

  function handleSelectBride(e) {
    var btn = e.currentTarget;
    var brideName = btn.dataset.brideName;

    if (!brideName) return;

    // Update URL with bride param (without reload)
    var url = new URL(window.location.href);
    url.searchParams.set('bride', brideName);
    if (btn.dataset.jobId) url.searchParams.set('job', btn.dataset.jobId);
    else url.searchParams.delete('job');
    window.history.pushState({}, '', url.toString());

    // Hide search, show skeleton, fetch the runsheet
    hideSearch();
    waitForBridge(function() {
      fetchFull();
    });
  }

  function formatSearchDate(isoDate) {
    if (!isoDate) return '';
    try {
      // Parse YYYY-MM-DD or DD/MM/YYYY
      var parts;
      if (/^\d{4}-\d{2}-\d{2}/.test(isoDate)) {
        parts = isoDate.split('-');
        var d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      } else {
        parts = isoDate.split('/');
        var d = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
      }
      if (isNaN(d.getTime())) return isoDate;

      var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      var days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return days[d.getDay()] + ' ' + d.getDate() + ' ' + months[d.getMonth()] + ' ' + d.getFullYear();
    } catch (e) { return isoDate; }
  }

  /* ═══════════════════════════════════════════════════════════
     EVENT BINDING
     ═══════════════════════════════════════════════════════════ */

  function bindEvents() {
    // Retry
    if ($retryBtn) $retryBtn.addEventListener('click', fetchFull);

    // Search input (team audience)
    if ($searchInput) {
      $searchInput.addEventListener('input', handleSearchInput);
      $searchInput.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (searchDebounce) clearTimeout(searchDebounce);
          var term = $searchInput.value.trim();
          if (term.length >= 2) performSearch(term);
        }
      });
    }

    // Back to search button (team audience)
    var $backBtn = document.getElementById('jvRvBackBtn');
    if ($backBtn) {
      $backBtn.addEventListener('click', function() {
        // Stop polling
        if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
        // Reset state
        currentData = null;
        currentEtag = null;
        // Remove bride from URL
        var url = new URL(window.location.href);
        url.searchParams.delete('bride');
        url.searchParams.delete('brideName');
        window.history.pushState({}, '', url.toString());
        // Show search
        showSearch();
      });
    }

    // Handle browser back/forward
    window.addEventListener('popstate', function() {
      var params = new URLSearchParams(window.location.search);
      var hasBride = params.get('bride') || params.get('brideName');
      if (audience === 'team' && !hasBride) {
        if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
        currentData = null;
        currentEtag = null;
        showSearch();
      } else if (hasBride) {
        hideSearch();
        waitForBridge(function() { fetchFull(); });
      }
    });

    // Modal close
    if ($modalClose) $modalClose.addEventListener('click', closeModal);
    if ($modalBackdrop) $modalBackdrop.addEventListener('click', function(e) {
      if (e.target === $modalBackdrop) closeModal();
    });

    // Lightbox
    if ($lightboxClose) $lightboxClose.addEventListener('click', closeLightbox);
    if ($lightboxPrev) $lightboxPrev.addEventListener('click', function() { lightboxNav(-1); });
    if ($lightboxNext) $lightboxNext.addEventListener('click', function() { lightboxNav(1); });
    if ($lightbox) $lightbox.addEventListener('click', function(e) {
      if (e.target === $lightbox) closeLightbox();
    });

    // Toast
    if ($toastClose) $toastClose.addEventListener('click', hideToast);
    if ($toastAction) $toastAction.addEventListener('click', function() {
      hideToast();
      highlightDiffBlocks();
    });

    // Bridal party toggle
    if ($partyToggle) $partyToggle.addEventListener('click', function() {
      $party.classList.toggle('jv-rv__party--open');
    });

    // Keyboard
    document.addEventListener('keydown', function(e) {
      // Escape closes modals/lightbox
      if (e.key === 'Escape') {
        if ($lightbox.style.display !== 'none') { closeLightbox(); return; }
        if ($modalBackdrop.style.display !== 'none') { closeModal(); return; }
        return;
      }

      // Arrow keys for lightbox
      if ($lightbox.style.display !== 'none') {
        if (e.key === 'ArrowLeft') { lightboxNav(-1); e.preventDefault(); }
        if (e.key === 'ArrowRight') { lightboxNav(1); e.preventDefault(); }
        return;
      }

      // Arrow keys for artist pills
      if (document.activeElement && document.activeElement.classList.contains('jv-rv__artist-pill')) {
        var pills = Array.from($artistRail.querySelectorAll('.jv-rv__artist-pill'));
        var idx = pills.indexOf(document.activeElement);
        if (e.key === 'ArrowLeft' && idx > 0) { pills[idx - 1].focus(); e.preventDefault(); }
        if (e.key === 'ArrowRight' && idx < pills.length - 1) { pills[idx + 1].focus(); e.preventDefault(); }
      }
    });

    // "All" pill click
    var allPill = document.getElementById('jvRvPillAll');
    if (allPill) allPill.addEventListener('click', handleArtistFilter);
  }

  /* ═══════════════════════════════════════════════════════════
     BOOT
     ═══════════════════════════════════════════════════════════ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
